import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiArrowLeftBold,
    PiArrowDownBold,
    PiArrowUpBold,
    PiMinusBold,
    PiArrowsLeftRightBold,
    PiArrowRightBold,
    PiXBold,
    PiSnowflakeBold,
    PiTableBold,
    PiCardsBold,
    PiCopyBold,
    PiCheckBold,
    PiMicrosoftExcelLogoBold,
    PiCaretUpDownBold,
    PiPlusBold,
    PiTrashBold,
    PiArrowCounterClockwiseBold,
    PiCaretLeftBold,
    PiCaretRightBold,
    PiInfoBold,
    PiWalletBold,
    PiClockBold,
    PiShieldCheckBold,
    PiCalendarBlankBold,
    PiFilePdfBold,
} from 'react-icons/pi';
import { Button, LoadingState, MultiSelect } from '@v2/design-system';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import api from '@v2/lib/api';
import { isSuperAdmin } from '@v2/lib/auth';
import { ploomesDealPath } from '@v2/features/ploomes-deals/dealPath';
import {
    buildHistoryExportRows,
    historyExportHeaders,
    toTsv,
    type HistoryExportLabels,
} from '@v2/features/commissioning/historyMonthExport';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import { COMMISSION_HOME_MONTH_QUERY } from '@v2/hooks/commissionLiveQuery';
import { fmtMonthShort } from './CommissionExecutiveCharts';
import ManualSaleInclusionModal from './ManualSaleInclusionModal';
import CommissionWalletPanel, { CommissionApprovalModal, useCommissionActionPermissions, WalletAppliedSection } from './CommissionWalletPanel';
import { MonthClosedBadge } from './MonthClosingCard';
import { downloadPdf } from '@v2/lib/downloadPdf';
import type {
    SellerCommissionForecast,
    HistoryPartyColumn,
    SellerHistoryMonthDetail,
    SellerHistoryMonthItem,
    SellerProfileMonth,
    SellerProfileTeam,
    SellerProfileUser,
} from './types';

const AVATAR_FALLBACK = '/img/favicon.png';

/** Toggle visual (não checkbox) — estado controlado pelo pai. */
function FutureMonthsToggle({
    checked,
    disabled,
    label,
    onChange,
}: {
    checked: boolean;
    disabled?: boolean;
    label: string;
    onChange: (next: boolean) => void;
}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            disabled={disabled}
            onClick={() => {
                if (disabled) return;
                onChange(!checked);
            }}
            className={cn(
                'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
                disabled && 'cursor-not-allowed opacity-50',
                checked
                    ? 'border-indigo-600 bg-indigo-600'
                    : 'border-gray-300 bg-gray-200 dark:border-zinc-600 dark:bg-zinc-700',
            )}
        >
            <span
                aria-hidden
                className={cn(
                    'inline-block size-5 rounded-full bg-white shadow transition-transform',
                    checked ? 'translate-x-[1.35rem]' : 'translate-x-0.5',
                )}
            />
        </button>
    );
}

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 }).format(v ?? 0);
const brlShort = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);

function initials(name: string) {
    return name.trim().split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
}

function SellerAvatar({ name, url, size = 'lg' }: { name: string; url?: string | null; size?: 'lg' | 'md' }) {
    const cls = size === 'lg'
        ? 'h-14 w-14 shrink-0 rounded-full text-lg ring-2 ring-white shadow-md dark:ring-zinc-800'
        : 'h-12 w-12 shrink-0 rounded-full text-base ring-2 ring-white shadow dark:ring-zinc-800';
    if (url) {
        return (
            <img
                src={url}
                alt={name}
                className={cn('object-cover', cls)}
                onError={(e) => { (e.target as HTMLImageElement).src = AVATAR_FALLBACK; }}
            />
        );
    }
    return (
        <span className={cn(
            'flex items-center justify-center font-bold bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
            cls,
        )}>
            {initials(name)}
        </span>
    );
}

function RolePartyAvatar({ name, url }: { name: string; url?: string | null }) {
    const cls = 'h-6 w-6 shrink-0 rounded-full text-[9px] ring-2 ring-white dark:ring-zinc-900';
    if (url) {
        return (
            <img
                src={url}
                alt={name}
                title={name}
                className={cn('object-cover', cls)}
                onError={(e) => { (e.target as HTMLImageElement).src = AVATAR_FALLBACK; }}
            />
        );
    }
    return (
        <span
            title={name}
            className={cn(
                'flex items-center justify-center font-bold bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300',
                cls,
            )}
        >
            {initials(name)}
        </span>
    );
}

function SalePartyCell({
    party,
}: {
    party?: { id?: number | null; name: string; avatar_url?: string | null } | null;
}) {
    if (!party?.name) {
        return <span className="text-xs text-gray-400">—</span>;
    }

    return (
        <div className="flex min-w-0 items-center gap-1.5" title={party.name}>
            <RolePartyAvatar name={party.name} url={party.avatar_url} />
            <span className="max-w-[132px] truncate text-xs font-medium text-gray-700 dark:text-zinc-200">
                {party.name}
            </span>
        </div>
    );
}

function RoleCell({ item }: { item: SellerHistoryMonthItem }) {
    const { t } = useI18n();
    const parties = item.role_parties ?? [];
    const title = parties.map((p) => p.name).filter(Boolean).join(' · ') || item.role_label;
    const isSdr = item.commission_role === 'sdr' || item.rate_basis === 'sdr_owner_base';
    const ownerName = parties.find((p) => p.role === 'vendedor')?.name;

    if (parties.length === 0) {
        return <span className="text-xs text-gray-400">—</span>;
    }

    return (
        <div className="flex flex-col gap-0.5" title={title}>
            <div className="flex items-center">
                <div className="flex shrink-0 -space-x-1.5">
                    {parties.map((p, idx) => (
                        <RolePartyAvatar
                            key={`${p.id ?? p.name}-${idx}`}
                            name={p.name}
                            url={p.avatar_url}
                        />
                    ))}
                </div>
            </div>
            {isSdr && (
                <span className="max-w-[140px] truncate text-[10px] font-semibold leading-tight text-violet-700 dark:text-violet-300">
                    {ownerName
                        ? t('v2.commissioning.history_month.sdr_role_hint_named', {
                              name: ownerName.split(' ')[0] ?? ownerName,
                          })
                        : t('v2.commissioning.history_month.sdr_role_hint')}
                </span>
            )}
        </div>
    );
}

function ValueCell({
    item,
    tone = 'default',
}: {
    item: SellerHistoryMonthItem;
    tone?: 'default' | 'danger';
}) {
    const { t } = useI18n();
    const isSdr = item.commission_role === 'sdr' || item.rate_basis === 'sdr_owner_base';
    const pill =
        tone === 'danger'
            ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200'
            : 'bg-slate-100 text-slate-800 dark:bg-zinc-700 dark:text-zinc-100';
    const hint =
        tone === 'danger'
            ? 'text-red-600/90 dark:text-red-300/90'
            : 'text-violet-700 dark:text-violet-300';

    return (
        <div className="text-right">
            <span
                className={cn(
                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums',
                    pill,
                )}
                title={
                    isSdr
                        ? t('v2.commissioning.history_month.sdr_value_title')
                        : undefined
                }
            >
                {item.installment_amount != null ? brl(item.installment_amount) : '—'}
            </span>
            {isSdr && item.basis_amount != null && (
                <span className={cn('mt-0.5 block text-[10px] font-medium tabular-nums', hint)}>
                    {t('v2.commissioning.history_month.sdr_basis_line', {
                        amount: brl(item.basis_amount),
                    })}
                </span>
            )}
        </div>
    );
}

function RateCell({
    item,
    tone = 'default',
}: {
    item: SellerHistoryMonthItem;
    tone?: 'default' | 'danger';
}) {
    const { t } = useI18n();
    const isSdr = item.commission_role === 'sdr' || item.rate_basis === 'sdr_owner_base';
    const taShare = item.collaborator_share_pct ?? null;
    const isTaCollab =
        (item.commission_role === 'colaborador' || item.rate_basis === 'team_agreement_pool') &&
        item.pool_rate_pp != null &&
        taShare != null &&
        taShare > 0 &&
        taShare < 100;
    const ownerName = item.role_parties?.find((p) => p.role === 'vendedor')?.name;
    const text =
        tone === 'danger'
            ? 'text-red-700 dark:text-red-300'
            : 'text-gray-600 dark:text-zinc-300';
    const hint =
        tone === 'danger'
            ? 'text-red-600/90 dark:text-red-300/90'
            : 'text-violet-700 dark:text-violet-300';

    const primaryRate = isTaCollab ? item.pool_rate_pp : item.commission_rate_pp;

    return (
        <div className={cn('text-right tabular-nums', text)}>
            <span className="font-semibold">{fmtRate(primaryRate)}</span>
            {isSdr && (
                <span className={cn('mt-0.5 block text-[10px] font-medium leading-tight', hint)}>
                    {ownerName
                        ? t('v2.commissioning.history_month.sdr_rate_hint_named', {
                              name: ownerName.split(' ')[0] ?? ownerName,
                          })
                        : t('v2.commissioning.history_month.sdr_rate_hint')}
                </span>
            )}
            {isTaCollab && taShare != null && (
                <span className={cn('mt-0.5 block text-[10px] font-medium leading-tight', hint)}>
                    {t('v2.commissioning.history_month.ta_rate_hint', {
                        share: String(Math.round(taShare * 100) / 100),
                    })}
                </span>
            )}
        </div>
    );
}

type MonthDelta = {
    month: string;
    paid: number;
    prev_paid: number;
    delta: number;
    delta_pct: number | null;
    direction: 'up' | 'down' | 'flat';
};
type TimelinePayload = {
    user: SellerProfileUser;
    team: SellerProfileTeam | null;
    months: SellerProfileMonth[];
    month_deltas: MonthDelta[];
    forecast: SellerCommissionForecast | null;
    summary: {
        paid_lifetime: number;
        pipeline: number;
        paid_ytd: number;
        forecast_horizon: number;
    };
};
type ComparePayload = {
    base: SellerHistoryMonthDetail;
    compare: SellerHistoryMonthDetail;
    delta: {
        paid: number;
        paid_pct: number | null;
        accrued: number;
        accrued_pct: number | null;
        paid_deals: number;
        direction_paid: 'up' | 'down' | 'flat';
    };
};

function formatDate(date: string | null | undefined) {
    if (!date) return '—';
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
}

function fmtMonthYear(ym: string) {
    const [y, m] = ym.split('-').map(Number);
    const short = new Date(y, m - 1, 1)
        .toLocaleDateString('pt-BR', { month: 'short' })
        .replace('.', '');
    const label = short.charAt(0).toUpperCase() + short.slice(1);
    return `${label} ${y}`;
}

function fmtMonthLong(ym: string) {
    const [y, m] = ym.split('-').map(Number);
    const month = new Date(y, m - 1, 1)
        .toLocaleDateString('pt-BR', { month: 'long' });
    const label = month.charAt(0).toUpperCase() + month.slice(1);
    return `${label} / ${y}`;
}

function installmentLabel(item: SellerHistoryMonthItem) {
    if (item.installment_number == null) return 'Deal';
    if (item.installment_number === 0) {
        return (item.entry_index ?? 0) > 0 ? `Entrada ${item.entry_index}` : 'Entrada';
    }
    return `${item.installment_number}/${item.total_installments || '—'}`;
}

const INSTALLMENT_MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** Mês de referência da parcela (vencimento do cliente), compacto: ago/2026. */
function installmentRefMonth(item: SellerHistoryMonthItem): string | null {
    const raw = item.due_date ?? item.client_paid_at ?? null;
    if (!raw) return null;
    const d = new Date(`${raw.slice(0, 10)}T12:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    return `${INSTALLMENT_MONTHS[d.getMonth()]}/${d.getFullYear()}`;
}

function InstallmentBadge({
    item,
    tone = 'violet',
}: {
    item: SellerHistoryMonthItem;
    tone?: 'violet' | 'red';
}) {
    const refMonth = installmentRefMonth(item);
    return (
        <span
            className={cn(
                'inline-flex whitespace-nowrap items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums',
                tone === 'violet' && 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300',
                tone === 'red' && 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
            )}
        >
            {installmentLabel(item)}
            {refMonth && (
                <span className="font-semibold opacity-70">{refMonth}</span>
            )}
        </span>
    );
}

function DirectionBadge({ direction, label }: { direction: 'up' | 'down' | 'flat'; label: string }) {
    const Icon = direction === 'up' ? PiArrowUpBold : direction === 'down' ? PiArrowDownBold : PiMinusBold;
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold',
                direction === 'up' && 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
                direction === 'down' && 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
                direction === 'flat' && 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-300',
            )}
        >
            <Icon className="h-3 w-3" />
            {label}
        </span>
    );
}

type StripMonth = {
    month: string;
    /** Comissão já na competência (pago/líquido do mês). */
    paid: number;
    /** Projeção de parcelas futuras neste mês. */
    projected: number;
    /** Valor exibido na faixa (pago se > 0, senão projeção). */
    display: number;
    isFuture: boolean;
};

function currentMonthKey() {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

/** Une histórico pago + meses futuros do forecast de parcelas.
 * Preferência: total por competência (mesma regra do detalhe) > projeção do cronograma.
 */
function buildStripMonths(
    history: SellerProfileMonth[],
    forecast: SellerCommissionForecast | null | undefined,
): StripMonth[] {
    const nowYm = currentMonthKey();
    const byMonth = new Map<string, StripMonth>();

    for (const m of history) {
        const paid = m.paid ?? 0;
        byMonth.set(m.month, {
            month: m.month,
            paid,
            projected: 0,
            display: paid,
            isFuture: m.month > nowYm,
        });
    }

    for (const f of forecast?.months ?? []) {
        const projected = f.amount ?? 0;
        const existing = byMonth.get(f.month);
        if (existing) {
            existing.projected = projected;
            // Só usa forecast quando ainda não há total por competência no mês.
            existing.display = existing.paid > 0.009 ? existing.paid : projected;
            existing.isFuture = f.month > nowYm;
        } else {
            byMonth.set(f.month, {
                month: f.month,
                paid: 0,
                projected,
                display: projected,
                isFuture: f.month > nowYm,
            });
        }
    }

    return [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
}

/** Barra horizontal de meses — CTA para filtrar o detalhe da tela. */
function MonthStrip({
    months,
    selected,
    compare,
    onSelect,
}: {
    months: StripMonth[];
    selected: string;
    compare: string | null;
    onSelect: (ym: string) => void;
}) {
    const { t } = useI18n();
    const scrollerRef = useRef<HTMLDivElement>(null);

    const scrollBy = (dir: -1 | 1) => {
        scrollerRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });
    };

    useEffect(() => {
        const root = scrollerRef.current;
        if (!root) return;
        const el = root.querySelector<HTMLElement>(`[data-month="${selected}"]`);
        el?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }, [selected, months]);

    return (
        <section className="rounded-2xl border border-gray-200 bg-white px-2 py-3 dark:border-zinc-700 dark:bg-zinc-900 sm:px-3">
            <div className="flex items-center gap-1 sm:gap-2">
                <button
                    type="button"
                    onClick={() => scrollBy(-1)}
                    className="flex size-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:hover:bg-zinc-700"
                    aria-label={t('v2.commissioning.timeline.months_prev')}
                >
                    <PiCaretLeftBold className="size-4" />
                </button>

                <div
                    ref={scrollerRef}
                    className="flex min-w-0 flex-1 gap-2 overflow-x-auto scroll-smooth px-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                    {months.map((m) => {
                        const isSel = m.month === selected;
                        const isCmp = m.month === compare;
                        const hasValue = m.display > 0;
                        return (
                            <button
                                key={m.month}
                                type="button"
                                data-month={m.month}
                                onClick={() => onSelect(m.month)}
                                className={cn(
                                    'flex min-w-[88px] shrink-0 flex-col items-center rounded-xl border px-3 py-2.5 text-center transition',
                                    isSel && 'border-emerald-500 bg-emerald-50 shadow-sm dark:border-emerald-500 dark:bg-emerald-950/40',
                                    isCmp && !isSel && 'border-sky-400 bg-sky-50 dark:border-sky-600 dark:bg-sky-950/30',
                                    !isSel && !isCmp && 'border-transparent bg-transparent hover:bg-gray-50 dark:hover:bg-zinc-800/60',
                                )}
                            >
                                <span className={cn(
                                    'text-[11px] font-semibold whitespace-nowrap',
                                    isSel ? 'text-emerald-800 dark:text-emerald-200' : 'text-gray-500 dark:text-zinc-400',
                                )}>
                                    {fmtMonthYear(m.month)}
                                </span>
                                <span className={cn(
                                    'mt-1 text-sm font-bold tabular-nums',
                                    isSel && 'text-emerald-700 dark:text-emerald-300',
                                    !isSel && hasValue && m.isFuture && 'text-orange-600 dark:text-orange-400',
                                    !isSel && hasValue && !m.isFuture && 'text-gray-900 dark:text-white',
                                    !isSel && !hasValue && 'text-gray-300 dark:text-zinc-600',
                                )}>
                                    {hasValue ? brlShort(m.display) : '--'}
                                </span>
                                <span
                                    className={cn(
                                        'mt-1.5 size-1.5 rounded-full',
                                        hasValue
                                            ? (m.isFuture && !isSel ? 'bg-orange-400' : 'bg-emerald-500')
                                            : 'bg-transparent',
                                    )}
                                    aria-hidden
                                />
                            </button>
                        );
                    })}
                </div>

                <button
                    type="button"
                    onClick={() => scrollBy(1)}
                    className="flex size-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:hover:bg-zinc-700"
                    aria-label={t('v2.commissioning.timeline.months_next')}
                >
                    <PiCaretRightBold className="size-4" />
                </button>
            </div>
            <p className="mt-2 flex items-center justify-center gap-1.5 px-2 text-[11px] text-gray-400 dark:text-zinc-500">
                <PiInfoBold className="size-3.5 shrink-0" aria-hidden />
                {t('v2.commissioning.timeline.months_help')}
            </p>
        </section>
    );
}

function SummaryHero({
    user,
    team,
    monthCommission,
    futureAmount,
    futureCount,
    defaultedAmount,
    defaultedCount,
}: {
    user: SellerProfileUser;
    team: SellerProfileTeam | null;
    monthCommission: number;
    futureAmount: number;
    futureCount: number;
    defaultedAmount: number;
    defaultedCount: number;
}) {
    const { t } = useI18n();

    return (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900 lg:col-span-1">
                <div className="flex items-start gap-3">
                    <SellerAvatar name={user.name} url={user.avatar_url} size="md" />
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                            <h2 className="truncate text-sm font-bold text-gray-900 dark:text-white">
                                {user.name}
                            </h2>
                            <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
                                {t('v2.commissioning.timeline.badge_active')}
                            </span>
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                            {(team?.role_label || user.job_title) && (
                                <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-800 dark:bg-orange-900/40 dark:text-orange-200">
                                    {team?.role_label || user.job_title}
                                </span>
                            )}
                            {team?.team_name && (
                                <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">
                                    {team.team_name}
                                </span>
                            )}
                            {team?.manager_name && (
                                <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-800 dark:bg-violet-900/40 dark:text-violet-200">
                                    {t('v2.commissioning.timeline.manager_tag', {
                                        name: team.manager_name.split(' ')[0],
                                    })}
                                </span>
                            )}
                        </div>
                        {user.email && (
                            <p className="mt-1.5 truncate text-[11px] text-gray-400 dark:text-zinc-500">
                                {user.email}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <HeroKpi
                tone="emerald"
                label={t('v2.commissioning.timeline.kpi_month')}
                value={brlShort(monthCommission)}
                hint={t('v2.commissioning.timeline.kpi_month_hint')}
                icon={PiWalletBold}
            />
            <HeroKpi
                tone="orange"
                label={t('v2.commissioning.timeline.kpi_future')}
                value={brlShort(futureAmount)}
                hint={
                    futureCount > 0
                        ? t('v2.commissioning.timeline.kpi_future_hint', { count: String(futureCount) })
                        : t('v2.commissioning.timeline.kpi_future_empty')
                }
                icon={PiClockBold}
            />
            <HeroKpi
                tone="violet"
                label={t('v2.commissioning.timeline.kpi_defaulted')}
                value={brlShort(defaultedAmount)}
                hint={
                    defaultedCount > 0
                        ? t('v2.commissioning.timeline.kpi_defaulted_hint', { count: String(defaultedCount) })
                        : t('v2.commissioning.timeline.kpi_defaulted_empty')
                }
                icon={defaultedAmount > 0 ? PiShieldCheckBold : PiCheckBold}
            />
        </div>
    );
}

function HeroKpi({
    label,
    value,
    hint,
    tone,
    icon: Icon,
}: {
    label: string;
    value: string;
    hint: string;
    tone: 'emerald' | 'orange' | 'violet';
    icon: typeof PiWalletBold;
}) {
    const toneMap = {
        emerald: {
            card: 'border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900',
            value: 'text-emerald-600 dark:text-emerald-400',
            iconWrap: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-300',
        },
        orange: {
            card: 'border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900',
            value: 'text-orange-600 dark:text-orange-400',
            iconWrap: 'bg-orange-50 text-orange-600 dark:bg-orange-950/50 dark:text-orange-300',
        },
        violet: {
            card: 'border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900',
            value: 'text-violet-600 dark:text-violet-400',
            iconWrap: 'bg-violet-50 text-violet-600 dark:bg-violet-950/50 dark:text-violet-300',
        },
    }[tone];

    return (
        <div className={cn('flex items-center justify-between gap-3 rounded-2xl border p-4', toneMap.card)}>
            <div className="min-w-0">
                <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                    {label}
                    <PiInfoBold className="size-3 opacity-60" aria-hidden />
                </p>
                <p className={cn('mt-1 text-2xl font-black tabular-nums tracking-tight', toneMap.value)}>
                    {value}
                </p>
                <p className="mt-0.5 text-xs text-gray-400 dark:text-zinc-500">{hint}</p>
            </div>
            <span className={cn('flex size-11 shrink-0 items-center justify-center rounded-full', toneMap.iconWrap)}>
                <Icon className="size-5" aria-hidden />
            </span>
        </div>
    );
}

function MonthComparator({
    months,
    selected,
    compare,
    onSelect,
    onCompare,
    onSwap,
}: {
    months: StripMonth[];
    selected: string;
    compare: string | null;
    onSelect: (ym: string) => void;
    onCompare: (ym: string | null) => void;
    onSwap: () => void;
}) {
    const { t } = useI18n();
    const options = [...months].reverse();

    const optionLabel = (m: StripMonth) => {
        const amount = m.display > 0 ? brlShort(m.display) : 'R$ 0';
        const tag = m.isFuture
            ? t('v2.commissioning.timeline.compare_opt_projected')
            : t('v2.commissioning.timeline.compare_opt_paid');
        return `${fmtMonthShort(m.month)} — ${amount} ${tag}`;
    };

    return (
        <section className="rounded-2xl border border-sky-200 bg-sky-50/50 p-4 dark:border-sky-800/50 dark:bg-sky-950/20">
            <div className="mb-3">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {t('v2.commissioning.timeline.compare_title')}
                </h2>
                <p className="text-xs text-gray-500">{t('v2.commissioning.timeline.compare_help')}</p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <label className="min-w-0 flex-1">
                    <span className="mb-1 block text-[10px] font-black uppercase tracking-widest text-orange-700 dark:text-orange-300">
                        {t('v2.commissioning.timeline.compare_base')}
                    </span>
                    <select
                        value={selected}
                        onChange={(e) => onSelect(e.target.value)}
                        className="w-full rounded-xl border border-orange-200 bg-white px-3 py-2.5 text-sm font-semibold capitalize text-gray-800 outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400/30 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
                    >
                        {options.map((m) => (
                            <option key={m.month} value={m.month}>
                                {optionLabel(m)}
                            </option>
                        ))}
                    </select>
                </label>

                <div className="flex items-center justify-center gap-2 sm:pb-1">
                    <button
                        type="button"
                        onClick={onSwap}
                        disabled={!compare || compare === selected}
                        title={t('v2.commissioning.timeline.compare_swap')}
                        className="inline-flex size-10 items-center justify-center rounded-xl border border-sky-200 bg-white text-sky-700 transition hover:bg-sky-50 disabled:opacity-40 dark:border-sky-800 dark:bg-zinc-900 dark:text-sky-300"
                    >
                        <PiArrowsLeftRightBold className="size-4" />
                    </button>
                </div>

                <label className="min-w-0 flex-1">
                    <span className="mb-1 block text-[10px] font-black uppercase tracking-widest text-sky-700 dark:text-sky-300">
                        {t('v2.commissioning.timeline.compare_with')}
                    </span>
                    <select
                        value={compare ?? ''}
                        onChange={(e) => onCompare(e.target.value || null)}
                        className="w-full rounded-xl border border-sky-200 bg-white px-3 py-2.5 text-sm font-semibold capitalize text-gray-800 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400/30 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
                    >
                        <option value="">{t('v2.commissioning.timeline.compare_none')}</option>
                        {options
                            .filter((m) => m.month !== selected)
                            .map((m) => (
                                <option key={m.month} value={m.month}>
                                    {optionLabel(m)}
                                </option>
                            ))}
                    </select>
                </label>

                {compare && (
                    <button
                        type="button"
                        onClick={() => onCompare(null)}
                        className="inline-flex items-center gap-1 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-300"
                    >
                        <PiXBold className="size-3.5" />
                        {t('v2.commissioning.timeline.clear_compare')}
                    </button>
                )}
            </div>

            {compare && (
                <p className="mt-3 text-xs font-medium text-sky-800 dark:text-sky-200">
                    {t('v2.commissioning.timeline.compare_active', {
                        base: fmtMonthShort(selected),
                        other: fmtMonthShort(compare),
                    })}
                </p>
            )}
        </section>
    );
}

function fmtRate(pp: number | null | undefined) {
    if (pp == null || Number.isNaN(pp)) return '—';
    const rounded = Math.round(pp * 100) / 100;
    return `${String(rounded).replace('.', ',')}%`;
}

/** Taxa principal da linha (mesma regra da RateCell) para o texto do “Copiar comissões”. */
function copyRatePp(item: SellerHistoryMonthItem): number | null {
    const taShare = item.collaborator_share_pct ?? null;
    const isTaCollab =
        (item.commission_role === 'colaborador' || item.rate_basis === 'team_agreement_pool') &&
        item.pool_rate_pp != null &&
        taShare != null &&
        taShare > 0 &&
        taShare < 100;

    return (isTaCollab ? item.pool_rate_pp : item.commission_rate_pp) ?? null;
}

function accentTextClass(accent: 'emerald' | 'orange' | 'sky') {
    return accent === 'emerald'
        ? 'text-emerald-700 dark:text-emerald-300'
        : accent === 'sky'
            ? 'text-sky-700 dark:text-sky-300'
            : 'text-orange-700 dark:text-orange-300';
}

function saleNumbers(item: SellerHistoryMonthItem): string[] {
    if (item.sale_numbers && item.sale_numbers.length > 0) {
        return item.sale_numbers.map(String);
    }
    return [String(item.display_sale_number || item.internal_number || item.deal_number || item.deal_id)];
}

function saleNumber(item: SellerHistoryMonthItem) {
    return saleNumbers(item).join(' · ');
}

/** Link interno do Hub (SPA /ploomes-deals), nunca URL externa do Ploomes. */
function hubDealPath(item: Pick<SellerHistoryMonthItem, 'deal_id' | 'display_sale_number' | 'internal_number'>) {
    return ploomesDealPath({
        id: item.deal_id,
        internal_number: item.display_sale_number || item.internal_number,
    });
}

function OpenDealButton({ item }: { item: SellerHistoryMonthItem }) {
    const { t } = useI18n();

    return (
        <Link to={hubDealPath(item)} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="outline" className="gap-1">
                {t('v2.commissioning.history_month.open')}
                <PiArrowRightBold className="size-3.5" aria-hidden />
            </Button>
        </Link>
    );
}

function clientPayDate(item: SellerHistoryMonthItem) {
    return item.client_paid_at ?? item.due_date ?? item.finish_date ?? null;
}

function isSdrItem(item: SellerHistoryMonthItem): boolean {
    return item.commission_role === 'sdr' || item.rate_basis === 'sdr_owner_base';
}

function saleTypeRowClass(item: SellerHistoryMonthItem): string {
    // SDR primeiro: fatia do vendedor — destaque próprio na tabela.
    if (isSdrItem(item)) {
        return 'bg-indigo-100/85 dark:bg-indigo-950/45';
    }
    const keys = new Set((item.mode_flags ?? []).map((f) => f.key));
    // Prioridade: Team Agreement > SAD > Buckler One
    if (keys.has('team')) {
        return 'bg-violet-100/80 dark:bg-violet-950/40';
    }
    if (keys.has('sad')) {
        return 'bg-sky-100/80 dark:bg-sky-950/40';
    }
    if (keys.has('buckler_one')) {
        return 'bg-amber-100/80 dark:bg-amber-950/35';
    }
    return '';
}

const SALE_TYPE_FLAG_KEYS = new Set(['sad', 'team', 'buckler_one']);

type OriginFilter = 'all' | 'legacy' | 'hub';
type TypeFilter = 'all' | 'team' | 'exception' | 'sad' | 'buckler_one' | 'carencia' | 'standard' | 'sdr';
type RoleFilter = 'all' | 'vendedor' | 'colaborador' | 'sdr' | 'gerente';
type RetroFilter = 'all' | 'yes' | 'no';

const ORIGIN_FILTERS: OriginFilter[] = ['all', 'legacy', 'hub'];
const TYPE_FILTERS: TypeFilter[] = ['all', 'team', 'exception', 'sad', 'buckler_one', 'sdr', 'carencia', 'standard'];
const ROLE_FILTERS: RoleFilter[] = ['all', 'vendedor', 'colaborador', 'sdr', 'gerente'];
const RETRO_FILTERS: RetroFilter[] = ['all', 'yes', 'no'];

function isRetroactiveItem(item: SellerHistoryMonthItem): boolean {
    return Boolean(item.is_retroactive)
        || (item.mode_flags ?? []).some((f) => f.key === 'retroactive');
}

function saleTypeFlags(item: SellerHistoryMonthItem) {
    return (item.mode_flags ?? []).filter((f) => SALE_TYPE_FLAG_KEYS.has(f.key));
}

/** Tipo especial (Team/SAD/Buckler One/SDR) ou badge "Padrão" para não deixar a célula vazia. */
function resolvedSaleTypeFlags(
    item: SellerHistoryMonthItem,
    standardLabel: string,
    sdrLabel = 'SDR',
) {
    const flags = [...saleTypeFlags(item)];
    if (isSdrItem(item)) {
        flags.unshift({ key: 'sdr', label: sdrLabel });
    }
    if (flags.length > 0) {
        return flags;
    }

    return [{ key: 'standard', label: standardLabel }];
}

function otherModeFlags(item: SellerHistoryMonthItem) {
    return (item.mode_flags ?? []).filter((f) => f.key !== 'legacy' && !SALE_TYPE_FLAG_KEYS.has(f.key));
}

function itemHasModeFlag(item: SellerHistoryMonthItem, key: string): boolean {
    if (key === 'legacy') {
        return Boolean(item.is_legacy_frozen)
            || (item.mode_flags ?? []).some((f) => f.key === 'legacy');
    }

    return (item.mode_flags ?? []).some((f) => f.key === key);
}

type HistoryPartyOption = {
    value: string;
    label: string;
    avatar_url?: string | null;
};

function partyFilterKey(party?: { id?: number | null; name: string } | null): string | null {
    if (!party?.name) {
        return null;
    }
    return party.id != null ? `id:${party.id}` : `name:${party.name}`;
}

function parsePartyFilterParam(raw: string | null): string[] {
    if (!raw) {
        return [];
    }

    return raw.split(',').map((part) => {
        try {
            return decodeURIComponent(part);
        } catch {
            return part;
        }
    }).filter(Boolean);
}

function writePartyFilterParam(params: URLSearchParams, key: string, values: string[]) {
    if (values.length === 0) {
        params.delete(key);
        return;
    }
    params.set(key, values.map((v) => encodeURIComponent(v)).join(','));
}

function matchesPartyFilter(
    item: SellerHistoryMonthItem,
    selected: string[],
    field: 'sale_seller' | 'sale_manager',
): boolean {
    if (selected.length === 0) {
        return true;
    }
    const key = partyFilterKey(item[field]);
    return key != null && selected.includes(key);
}

function matchesRoleFilter(item: SellerHistoryMonthItem, role: RoleFilter): boolean {
    if (role === 'all') {
        return true;
    }
    const current = (item.commission_role ?? '').toLowerCase();
    if (role === 'vendedor') {
        return ['vendedor', 'salesperson', 'seller'].includes(current);
    }
    if (role === 'colaborador') {
        return ['colaborador', 'collaborator'].includes(current);
    }

    return current === role;
}

function matchesHistoryFilters(
    item: SellerHistoryMonthItem,
    origin: OriginFilter,
    type: TypeFilter,
    role: RoleFilter = 'all',
    retro: RetroFilter = 'all',
    sellers: string[] = [],
    managers: string[] = [],
): boolean {
    if (! matchesRoleFilter(item, role)) {
        return false;
    }
    if (!matchesPartyFilter(item, sellers, 'sale_seller')) {
        return false;
    }
    if (!matchesPartyFilter(item, managers, 'sale_manager')) {
        return false;
    }
    if (origin === 'legacy' && !itemHasModeFlag(item, 'legacy')) {
        return false;
    }
    if (origin === 'hub' && itemHasModeFlag(item, 'legacy')) {
        return false;
    }

    if (retro === 'yes' && !isRetroactiveItem(item)) {
        return false;
    }
    if (retro === 'no' && isRetroactiveItem(item)) {
        return false;
    }

    if (type === 'all') {
        return true;
    }
    if (type === 'sdr') {
        return isSdrItem(item);
    }
    if (type === 'standard') {
        return !itemHasModeFlag(item, 'team')
            && !itemHasModeFlag(item, 'sad')
            && !itemHasModeFlag(item, 'buckler_one')
            && !itemHasModeFlag(item, 'exception')
            && !isSdrItem(item);
    }

    return itemHasModeFlag(item, type);
}

function HistoryFilterChip({
    active,
    label,
    count,
    onClick,
}: {
    active: boolean;
    label: string;
    count?: number;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-[11px] font-semibold leading-none transition',
                active
                    ? 'border-orange-500 bg-orange-500 text-white shadow-sm'
                    : 'border-gray-200/90 bg-white text-gray-600 hover:border-orange-300 hover:text-orange-700 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-orange-500/50',
            )}
        >
            <span>{label}</span>
            {count != null && (
                <span
                    className={cn(
                        'min-w-[1.15rem] rounded px-1 py-0.5 text-center text-[10px] font-bold tabular-nums leading-none',
                        active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500 dark:bg-zinc-700 dark:text-zinc-300',
                    )}
                >
                    {count}
                </span>
            )}
        </button>
    );
}

function HistoryFilterRow({
    label,
    children,
}: {
    label: string;
    children: ReactNode;
}) {
    return (
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-[7rem_minmax(0,1fr)] sm:items-start sm:gap-x-3">
            <div className="flex h-7 items-center sm:justify-end">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                    {label}
                </span>
            </div>
            <div className="flex flex-wrap content-start gap-1.5">
                {children}
            </div>
        </div>
    );
}

function HistoryPartyMultiSelect({
    label,
    placeholder,
    searchPlaceholder,
    value,
    options,
    onChange,
}: {
    label: string;
    placeholder: string;
    searchPlaceholder: string;
    value: string[];
    options: HistoryPartyOption[];
    onChange: (value: string[]) => void;
}) {
    const { t } = useI18n();

    return (
        <MultiSelect
            label={label}
            size="sm"
            searchable
            stickySearch
            clearable={value.length > 0}
            options={options}
            value={value}
            placeholder={placeholder}
            searchPlaceHolder={searchPlaceholder}
            className="w-64"
            selectClassName="min-h-9 w-64"
            suffixClassName="ms-auto shrink-0"
            dropdownClassName="z-[80] !w-64"
            optionCheckBox
            hideSelectedOptions={false}
            onChange={onChange}
            onClear={() => onChange([])}
            getOptionDisplayValue={(option: HistoryPartyOption) => (
                <div className="flex min-w-0 items-center gap-2 py-0.5">
                    <RolePartyAvatar name={option.label} url={option.avatar_url} />
                    <span className="truncate text-sm text-gray-800 dark:text-zinc-100">{option.label}</span>
                </div>
            )}
            displayValue={(selected, opts, handleClearItem) => {
                if (!selected.length) {
                    return <span className="min-w-0 flex-1 truncate text-start text-gray-400">{placeholder}</span>;
                }

                return (
                    <div className="flex min-w-0 flex-1 flex-wrap gap-1 py-0.5">
                        {selected.map((key) => {
                            const opt = opts.find((o) => o.value === key) as HistoryPartyOption | undefined;
                            const name = opt?.label ?? key;
                            return (
                                <span
                                    key={key}
                                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white py-0.5 pl-0.5 pr-1.5 text-xs dark:border-zinc-700 dark:bg-zinc-900"
                                >
                                    <RolePartyAvatar name={name} url={opt?.avatar_url} />
                                    <span className="max-w-[8rem] truncate font-medium text-gray-700 dark:text-zinc-200">
                                        {name}
                                    </span>
                                    {handleClearItem && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleClearItem(key);
                                            }}
                                            className="rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                            aria-label={t('v2.commissioning.history_month.filter_sale_remove', { name })}
                                        >
                                            <PiXBold className="h-3 w-3" />
                                        </button>
                                    )}
                                </span>
                            );
                        })}
                    </div>
                );
            }}
        />
    );
}

function HistoryMonthFilters({
    items,
    filterParties,
    origin,
    type,
    role,
    retro,
    sellers,
    managers,
    partyColumns = [],
    onOriginChange,
    onTypeChange,
    onRoleChange,
    onRetroChange,
    onSellersChange,
    onManagersChange,
}: {
    items: SellerHistoryMonthItem[];
    filterParties?: SellerHistoryMonthDetail['filter_parties'];
    origin: OriginFilter;
    type: TypeFilter;
    role: RoleFilter;
    retro: RetroFilter;
    sellers: string[];
    managers: string[];
    partyColumns?: HistoryPartyColumn[];
    onOriginChange: (v: OriginFilter) => void;
    onTypeChange: (v: TypeFilter) => void;
    onRoleChange: (v: RoleFilter) => void;
    onRetroChange: (v: RetroFilter) => void;
    onSellersChange: (v: string[]) => void;
    onManagersChange: (v: string[]) => void;
}) {
    const { t } = useI18n();

    const originCounts = useMemo(() => ({
        all: items.length,
        legacy: items.filter((i) => itemHasModeFlag(i, 'legacy')).length,
        hub: items.filter((i) => !itemHasModeFlag(i, 'legacy')).length,
    }), [items]);

    const typeCounts = useMemo(() => ({
        all: items.length,
        team: items.filter((i) => itemHasModeFlag(i, 'team')).length,
        exception: items.filter((i) => itemHasModeFlag(i, 'exception')).length,
        sad: items.filter((i) => itemHasModeFlag(i, 'sad')).length,
        buckler_one: items.filter((i) => itemHasModeFlag(i, 'buckler_one')).length,
        sdr: items.filter((i) => isSdrItem(i)).length,
        carencia: items.filter((i) => itemHasModeFlag(i, 'carencia')).length,
        standard: items.filter((i) => matchesHistoryFilters(i, 'all', 'standard')).length,
    }), [items]);

    const roleCounts = useMemo(() => ({
        all: items.length,
        vendedor: items.filter((i) => matchesRoleFilter(i, 'vendedor')).length,
        colaborador: items.filter((i) => matchesRoleFilter(i, 'colaborador')).length,
        sdr: items.filter((i) => matchesRoleFilter(i, 'sdr')).length,
        gerente: items.filter((i) => matchesRoleFilter(i, 'gerente')).length,
    }), [items]);

    const retroCounts = useMemo(() => ({
        all: items.length,
        yes: items.filter((i) => isRetroactiveItem(i)).length,
        no: items.filter((i) => !isRetroactiveItem(i)).length,
    }), [items]);

    const sellerOptions = useMemo(
        () => (filterParties?.sellers ?? []).flatMap((party) => {
            const value = partyFilterKey(party);
            return value ? [{ value, label: party.name, avatar_url: party.avatar_url }] : [];
        }),
        [filterParties],
    );
    const managerOptions = useMemo(
        () => (filterParties?.managers ?? []).flatMap((party) => {
            const value = partyFilterKey(party);
            return value ? [{ value, label: party.name, avatar_url: party.avatar_url }] : [];
        }),
        [filterParties],
    );
    const showSellerFilter = partyColumns.includes('seller');
    const showManagerFilter = partyColumns.includes('manager');

    const filteredTotal = useMemo(
        () => items
            .filter((i) => matchesHistoryFilters(i, origin, type, role, retro, sellers, managers))
            .reduce((s, i) => s + i.final_amount, 0),
        [items, origin, type, role, retro, sellers, managers],
    );
    const filteredCount = useMemo(
        () => items.filter((i) => matchesHistoryFilters(i, origin, type, role, retro, sellers, managers)).length,
        [items, origin, type, role, retro, sellers, managers],
    );
    const hasActive = origin !== 'all' || type !== 'all' || role !== 'all' || retro !== 'all'
        || sellers.length > 0 || managers.length > 0;

    return (
        <div className="mb-3 overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900/60">
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 dark:border-zinc-800">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-zinc-400">
                    {t('v2.commissioning.history_month.filters_title')}
                </p>
                {hasActive && (
                    <button
                        type="button"
                        onClick={() => {
                            onOriginChange('all');
                            onTypeChange('all');
                            onRoleChange('all');
                            onRetroChange('all');
                            onSellersChange([]);
                            onManagersChange([]);
                        }}
                        className="text-[11px] font-semibold text-orange-600 hover:underline dark:text-orange-400"
                    >
                        {t('v2.commissioning.history_month.filters_clear')}
                    </button>
                )}
            </div>

            <div className="space-y-3 px-3 py-3">
                {(showSellerFilter || showManagerFilter) && (
                    <div className="grid gap-3">
                        {showManagerFilter && (
                            <HistoryPartyMultiSelect
                                label={t('v2.commissioning.history_month.filter_sale_manager')}
                                placeholder={t('v2.commissioning.history_month.filter_sale_manager_placeholder')}
                                searchPlaceholder={t('v2.commissioning.history_month.filter_sale_manager_search')}
                                value={managers}
                                options={managerOptions}
                                onChange={onManagersChange}
                            />
                        )}
                        {showSellerFilter && (
                            <HistoryPartyMultiSelect
                                label={t('v2.commissioning.history_month.filter_sale_seller')}
                                placeholder={t('v2.commissioning.history_month.filter_sale_seller_placeholder')}
                                searchPlaceholder={t('v2.commissioning.history_month.filter_sale_seller_search')}
                                value={sellers}
                                options={sellerOptions}
                                onChange={onSellersChange}
                            />
                        )}
                    </div>
                )}
                <HistoryFilterRow label={t('v2.commissioning.history_month.filter_origin')}>
                    {ORIGIN_FILTERS.map((key) => (
                        <HistoryFilterChip
                            key={key}
                            active={origin === key}
                            label={t(`v2.commissioning.history_month.filter_origin_${key}`)}
                            count={originCounts[key]}
                            onClick={() => onOriginChange(key)}
                        />
                    ))}
                </HistoryFilterRow>

                <HistoryFilterRow label={t('v2.commissioning.history_month.filter_role')}>
                    {ROLE_FILTERS.map((key) => (
                        <HistoryFilterChip
                            key={key}
                            active={role === key}
                            label={t(`v2.commissioning.history_month.filter_role_${key}`)}
                            count={roleCounts[key]}
                            onClick={() => onRoleChange(key)}
                        />
                    ))}
                </HistoryFilterRow>

                <HistoryFilterRow label={t('v2.commissioning.history_month.filter_type')}>
                    {TYPE_FILTERS.map((key) => (
                        <HistoryFilterChip
                            key={key}
                            active={type === key}
                            label={t(`v2.commissioning.history_month.filter_type_${key}`)}
                            count={typeCounts[key]}
                            onClick={() => onTypeChange(key)}
                        />
                    ))}
                </HistoryFilterRow>

                <HistoryFilterRow label={t('v2.commissioning.history_month.filter_retro')}>
                    {RETRO_FILTERS.map((key) => (
                        <HistoryFilterChip
                            key={key}
                            active={retro === key}
                            label={t(`v2.commissioning.history_month.filter_retro_${key}`)}
                            count={retroCounts[key]}
                            onClick={() => onRetroChange(key)}
                        />
                    ))}
                </HistoryFilterRow>
            </div>

            {hasActive && (
                <div className="border-t border-gray-100 bg-gray-50/80 px-3 py-2 dark:border-zinc-800 dark:bg-zinc-800/40">
                    <p className="text-xs text-gray-500 dark:text-zinc-400">
                        {t('v2.commissioning.history_month.filters_summary', {
                            count: filteredCount,
                            total: brl(filteredTotal),
                        })}
                    </p>
                </div>
            )}
        </div>
    );
}

function ModeFlagBadge({ flag }: { flag: { key: string; label: string } }) {
    return (
        <span
            className={cn(
                'rounded-full px-1.5 py-0.5 text-[9px] font-semibold',
                flag.key === 'exception' && 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
                flag.key === 'team' && 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300',
                flag.key === 'sad' && 'bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300',
                flag.key === 'buckler_one' && 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300',
                flag.key === 'sdr' && 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300',
                flag.key === 'floor' && 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
                flag.key === 'carencia' && 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
                flag.key === 'defaulted' && 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
                flag.key === 'retroactive' && 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
                flag.key === 'manual' && 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-950 dark:text-fuchsia-300',
                flag.key === 'standard' && 'border border-gray-200 bg-gray-50 text-gray-500 dark:border-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-400',
                !['exception', 'team', 'sad', 'buckler_one', 'sdr', 'floor', 'carencia', 'standard', 'defaulted', 'manual', 'retroactive'].includes(flag.key)
                    && 'bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-zinc-200',
            )}
        >
            {flag.label}
        </span>
    );
}

type SortKey =
    | 'sale'
    | 'sale_type'
    | 'deal'
    | 'installment'
    | 'client'
    | 'competence'
    | 'role'
    | 'sale_seller'
    | 'sale_manager'
    | 'value'
    | 'rate'
    | 'commission';

const NUMERIC_SORT_KEYS: SortKey[] = ['value', 'rate', 'commission', 'installment'];

/** Tabela plana (todas as linhas do mês) — para conferência com planilha. */
function FlatMonthTable({
    items,
    defaultedItems = [],
    forfeitedItems = [],
    accent,
    sellerName = '',
    sellerUserId,
    month,
    partyColumns = [],
    onRemoveManual,
    onExcludeFromReport,
    onClearDefault,
    walletApplied = 0,
}: {
    items: SellerHistoryMonthItem[];
    defaultedItems?: SellerHistoryMonthItem[];
    forfeitedItems?: SellerHistoryMonthItem[];
    accent: 'emerald' | 'orange' | 'sky';
    /** Estornos da carteira abatidos neste mês (já com o que foi remanejado). */
    walletApplied?: number;
    sellerName?: string;
    sellerUserId?: number | null;
    month?: string;
    partyColumns?: HistoryPartyColumn[];
    onRemoveManual?: (inclusionId: number) => void;
    onExcludeFromReport?: (item: SellerHistoryMonthItem) => void;
    onClearDefault?: (item: SellerHistoryMonthItem) => void;
}) {
    const { t } = useI18n();
    const queryClient = useQueryClient();
    const { canUncancel } = useCommissionActionPermissions();
    const [uncancelItem, setUncancelItem] = useState<SellerHistoryMonthItem | null>(null);
    const [sortKey, setSortKey] = useState<SortKey>('sale');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const [copied, setCopied] = useState(false);
    const [exporting, setExporting] = useState(false);

    const exportLabels = useMemo<HistoryExportLabels>(() => ({
        seller: t('v2.commissioning.history_month.export_col_seller'),
        sale: t('v2.commissioning.history_month.col_sale'),
        origin: t('v2.commissioning.history_month.col_source'),
        type: t('v2.commissioning.history_month.col_sale_type'),
        deal: t('v2.commissioning.history_month.col_deal'),
        ploomesNumber: t('v2.commissioning.history_month.export_col_ploomes'),
        hubLink: t('v2.commissioning.history_month.export_col_hub_link'),
        ploomesLink: t('v2.commissioning.history_month.export_col_ploomes_link'),
        installment: t('v2.commissioning.history_month.col_installment'),
        clientDate: t('v2.commissioning.history_month.col_client_date'),
        competence: t('v2.commissioning.history_month.col_competence'),
        role: t('v2.commissioning.history_month.col_role'),
        saleSeller: t('v2.commissioning.history_month.col_sale_seller'),
        saleManager: t('v2.commissioning.history_month.col_sale_manager'),
        value: t('v2.commissioning.history_month.col_value'),
        rate: t('v2.commissioning.history_month.col_rate'),
        commission: t('v2.commissioning.history_month.col_commission'),
        flags: t('v2.commissioning.history_month.export_col_flags'),
        status: t('v2.commissioning.history_month.export_col_status'),
        originHub: t('v2.commissioning.history_month.badge_hub'),
        originLegacy: t('v2.commissioning.history_month.badge_legacy'),
        typeStandard: t('v2.commissioning.history_month.badge_standard'),
        typeSdr: t('v2.commissioning.history_month.badge_sdr'),
        statusNormal: t('v2.commissioning.history_month.export_status_normal'),
        statusDefaulted: t('v2.commissioning.history_month.export_status_defaulted'),
        statusForfeited: t('v2.commissioning.history_month.export_status_forfeited'),
        badgeRetroactive: t('v2.commissioning.history_month.badge_retroactive'),
        entry: t('v2.commissioning.history_month.export_entry'),
        dealLevel: t('v2.commissioning.history_month.export_deal_level'),
    }), [t]);

    const sorted = useMemo(() => {
        const list = [...items];
        const dir = sortDir === 'asc' ? 1 : -1;
        const cmpStr = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }) * dir;
        const cmpNum = (a: number, b: number) => (a - b) * dir;
        list.sort((a, b) => {
            let primary = 0;
            switch (sortKey) {
                case 'sale':
                    primary = cmpStr(saleNumber(a), saleNumber(b));
                    break;
                case 'sale_type': {
                    const standardLabel = t('v2.commissioning.history_month.badge_standard');
                    const sdrLabel = t('v2.commissioning.history_month.badge_sdr');
                    primary = cmpStr(
                        resolvedSaleTypeFlags(a, standardLabel, sdrLabel).map((f) => f.label).join(', '),
                        resolvedSaleTypeFlags(b, standardLabel, sdrLabel).map((f) => f.label).join(', '),
                    );
                    break;
                }
                case 'deal':
                    primary = cmpStr(a.deal_title ?? '', b.deal_title ?? '');
                    break;
                case 'installment':
                    primary = cmpNum(a.installment_number ?? -1, b.installment_number ?? -1);
                    break;
                case 'client':
                    primary = cmpStr(clientPayDate(a) ?? '', clientPayDate(b) ?? '');
                    break;
                case 'competence':
                    primary = cmpStr(a.paid_at ?? '', b.paid_at ?? '');
                    break;
                case 'role':
                    primary = cmpStr(a.role_label ?? '', b.role_label ?? '');
                    break;
                case 'sale_seller':
                    primary = cmpStr(a.sale_seller?.name ?? '', b.sale_seller?.name ?? '');
                    break;
                case 'sale_manager':
                    primary = cmpStr(a.sale_manager?.name ?? '', b.sale_manager?.name ?? '');
                    break;
                case 'value':
                    primary = cmpNum(a.installment_amount ?? 0, b.installment_amount ?? 0);
                    break;
                case 'rate':
                    primary = cmpNum(copyRatePp(a) ?? 0, copyRatePp(b) ?? 0);
                    break;
                case 'commission':
                    primary = cmpNum(a.final_amount ?? 0, b.final_amount ?? 0);
                    break;
                default:
                    primary = 0;
            }
            if (primary !== 0) return primary;
            return cmpStr(saleNumber(a), saleNumber(b));
        });
        return list;
    }, [items, sortDir, sortKey, t]);

    const exportRows = useMemo(() => {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const normal = buildHistoryExportRows(sorted, {
            sellerName: sellerName || '—',
            labels: exportLabels,
            appOrigin: origin,
            isDefaulted: false,
            startIndex: 1,
            partyColumns,
        });
        const defaulted = buildHistoryExportRows(defaultedItems, {
            sellerName: sellerName || '—',
            labels: exportLabels,
            appOrigin: origin,
            isDefaulted: true,
            startIndex: normal.length + 1,
            partyColumns,
        });
        const forfeited = buildHistoryExportRows(forfeitedItems, {
            sellerName: sellerName || '—',
            labels: exportLabels,
            appOrigin: origin,
            isDefaulted: false,
            startIndex: normal.length + defaulted.length + 1,
            partyColumns,
        });
        return [...normal, ...defaulted, ...forfeited];
    }, [sorted, defaultedItems, forfeitedItems, sellerName, exportLabels, partyColumns]);

    const copyCommissions = async () => {
        const text = toTsv(historyExportHeaders(exportLabels, partyColumns), exportRows);
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            toast.success(t('v2.commissioning.history_month.copy_excel_ok'));
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error(t('v2.commissioning.history_month.copy_excel_error'));
        }
    };

    const extractExcel = async () => {
        if (!month || exporting) return;
        setExporting(true);
        try {
            const path = sellerUserId
                ? `/commissioning/sellers/${sellerUserId}/profile/months/${month}/export-xlsx`
                : `/commissioning/my-seller-profile/months/${month}/export-xlsx`;
            const response = await api.post(
                path,
                {
                    record_ids: sorted.map((i) => i.record_id),
                    defaulted_record_ids: defaultedItems.map((i) => i.record_id),
                },
                { responseType: 'blob' },
            );
            const blob = response.data as Blob;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            const disposition = String(response.headers?.['content-disposition'] ?? '');
            const match = disposition.match(/filename="?([^"]+)"?/i);
            a.href = url;
            a.download = match?.[1] || `comissao-${month}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(t('v2.commissioning.history_month.extract_excel_ok'));
        } catch {
            toast.error(t('v2.commissioning.history_month.extract_excel_error'));
        } finally {
            setExporting(false);
        }
    };

    const total = useMemo(() => items.reduce((s, i) => s + i.final_amount, 0), [items]);
    const defaultedTotal = useMemo(
        () => defaultedItems.reduce((s, i) => s + i.final_amount, 0),
        [defaultedItems],
    );
    const retroactiveItems = useMemo(
        () => items.filter((i) => i.is_retroactive || i.mode_flags?.some((f) => f.key === 'retroactive')),
        [items],
    );
    const retroactiveTotal = useMemo(
        () => retroactiveItems.reduce((s, i) => s + i.final_amount, 0),
        [retroactiveItems],
    );
    const normalTotal = total - retroactiveTotal;
    // Líquido = comissões pagáveis (normais + retroativos) − estornos do mês, igual ao "Líquido após wallet".
    // Inadimplência já ficou de fora do `total`.
    const hasWallet = Math.abs(walletApplied) > 0.009;
    const netTotal = Math.max(0, total - walletApplied);
    const grossTotal = total + defaultedTotal;
    const showRoleColumn = partyColumns.length === 0;
    const labelColSpan = showRoleColumn ? 10 : 9 + partyColumns.length;
    const hasDefaulted = defaultedItems.length > 0;
    const hasForfeited = forfeitedItems.length > 0;
    const forfeitedTotal = useMemo(
        () => forfeitedItems.reduce((s, i) => s + i.final_amount, 0),
        [forfeitedItems],
    );

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setSortKey(key);
        setSortDir(NUMERIC_SORT_KEYS.includes(key) ? 'desc' : 'asc');
    };

    const sortIcon = (key: SortKey) => {
        if (sortKey !== key) {
            return <PiCaretUpDownBold className="inline size-3.5 opacity-35" aria-hidden />;
        }
        return sortDir === 'asc'
            ? <PiArrowUpBold className="inline size-3.5 text-orange-600" />
            : <PiArrowDownBold className="inline size-3.5 text-orange-600" />;
    };

    const SortTh = ({
        col,
        label,
        align = 'left',
    }: {
        col: SortKey;
        label: string;
        align?: 'left' | 'right';
    }) => (
        <th className={cn('px-3 py-2.5', align === 'right' ? 'text-right' : 'text-left')}>
            <button
                type="button"
                onClick={() => toggleSort(col)}
                className={cn(
                    'inline-flex items-center gap-1 rounded-md px-1 py-0.5 transition hover:bg-gray-200/70 hover:text-gray-800 dark:hover:bg-zinc-700 dark:hover:text-zinc-100',
                    align === 'right' && 'ml-auto',
                    sortKey === col && 'text-orange-700 dark:text-orange-300',
                )}
                title={t('v2.commissioning.history_month.sort_hint')}
            >
                {label}
                {sortIcon(col)}
            </button>
        </th>
    );

    const headerRowClassName =
        'border-gray-200 bg-gray-50/95 text-[10px] font-semibold uppercase tracking-wide text-gray-500 backdrop-blur dark:border-zinc-700 dark:bg-zinc-800/95 dark:text-zinc-400';

    const HeaderRow = ({ border }: { border: 'bottom' | 'top' }) => (
        <tr
            className={cn(
                headerRowClassName,
                border === 'bottom' ? 'border-b' : 'border-t-2 border-b',
            )}
        >
            <th className="w-10 px-2 py-2.5 text-center">#</th>
            <SortTh col="sale" label={t('v2.commissioning.history_month.col_sale')} />
            <SortTh col="sale_type" label={t('v2.commissioning.history_month.col_sale_type')} />
            <SortTh col="deal" label={t('v2.commissioning.history_month.col_deal')} />
            <th className="whitespace-nowrap px-3 py-2.5 text-left">
                <button
                    type="button"
                    onClick={() => toggleSort('installment')}
                    className={cn(
                        'inline-flex items-center gap-1 rounded-md px-1 py-0.5 transition hover:bg-gray-200/70 hover:text-gray-800 dark:hover:bg-zinc-700 dark:hover:text-zinc-100',
                        sortKey === 'installment' && 'text-orange-700 dark:text-orange-300',
                    )}
                    title={t('v2.commissioning.history_month.sort_hint')}
                >
                    {t('v2.commissioning.history_month.col_installment')}
                    {sortIcon('installment')}
                </button>
            </th>
            <SortTh col="client" label={t('v2.commissioning.history_month.col_client_date')} />
            <SortTh col="competence" label={t('v2.commissioning.history_month.col_competence')} />
            {showRoleColumn && (
                <SortTh col="role" label={t('v2.commissioning.history_month.col_role')} />
            )}
            {partyColumns.includes('seller') && (
                <SortTh col="sale_seller" label={t('v2.commissioning.history_month.col_sale_seller')} />
            )}
            {partyColumns.includes('manager') && (
                <SortTh col="sale_manager" label={t('v2.commissioning.history_month.col_sale_manager')} />
            )}
            <SortTh col="value" label={t('v2.commissioning.history_month.col_value')} align="right" />
            <SortTh col="rate" label={t('v2.commissioning.history_month.col_rate')} align="right" />
            <SortTh col="commission" label={t('v2.commissioning.history_month.col_commission')} align="right" />
            <th className="px-3 py-2.5 text-right" />
        </tr>
    );

    if (items.length === 0 && !hasDefaulted && !hasForfeited) {
        return (
            <p className="py-6 text-center text-sm text-gray-400">
                {t('v2.commissioning.history_month.empty')}
            </p>
        );
    }

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-gray-500">
                    {t('v2.commissioning.history_month.view_table_hint')}
                    {' · '}
                    {t('v2.commissioning.history_month.rows_count', {
                        count: items.length + defaultedItems.length + forfeitedItems.length,
                    })}
                    {hasDefaulted && (
                        <span className="text-red-600 dark:text-red-400">
                            {' · '}
                            {t('v2.commissioning.history_month.defaulted_rows_hint', {
                                count: defaultedItems.length,
                            })}
                        </span>
                    )}
                    {hasForfeited && (
                        <span className="text-violet-700 dark:text-violet-300">
                            {' · '}
                            {t('v2.commissioning.history_month.forfeited_badge')} ({forfeitedItems.length})
                        </span>
                    )}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => void copyCommissions()}
                        className="gap-1.5"
                        disabled={exportRows.length === 0}
                    >
                        {copied ? <PiCheckBold className="size-3.5" /> : <PiCopyBold className="size-3.5" />}
                        {copied
                            ? t('v2.commissioning.history_month.copied')
                            : t('v2.commissioning.history_month.copy_commissions')}
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => void extractExcel()}
                        className="gap-1.5"
                        disabled={!month || exporting || exportRows.length === 0}
                        isLoading={exporting}
                    >
                        <PiMicrosoftExcelLogoBold className="size-3.5" />
                        {t('v2.commissioning.history_month.extract_excel')}
                    </Button>
                </div>
            </div>
            <div className="max-h-[min(70vh,48rem)] overflow-auto rounded-xl border border-gray-200 dark:border-zinc-700">
                <table className="min-w-[960px] w-full text-sm">
                    <thead className="sticky top-0 z-20 shadow-sm">
                        <HeaderRow border="bottom" />
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                        {sorted.map((item, index) => (
                            <tr
                                key={item.record_id}
                                className={cn(
                                    'hover:brightness-[0.98] dark:hover:brightness-110',
                                    saleTypeRowClass(item) || 'hover:bg-gray-50/80 dark:hover:bg-zinc-800/40',
                                )}
                            >
                                <td className="px-2 py-2 text-center text-xs font-bold tabular-nums text-gray-400">
                                    {index + 1}
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        {saleNumbers(item).map((n) => (
                                            <span
                                                key={n}
                                                className="inline-flex items-center rounded-full bg-gray-900 px-2.5 py-0.5 text-xs font-bold tabular-nums text-white dark:bg-zinc-100 dark:text-zinc-900"
                                            >
                                                {n}
                                            </span>
                                        ))}
                                        {item.is_legacy_frozen ? (
                                            <span
                                                className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-100 px-2 py-0.5 text-[10px] font-black text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/20 dark:text-sky-300"
                                                title={t('v2.ploomes_deals.badges.commission_frozen_hint')}
                                            >
                                                <PiSnowflakeBold className="size-3 shrink-0" aria-hidden />
                                                {t('v2.commissioning.history_month.badge_legacy')}
                                            </span>
                                        ) : (
                                            <span
                                                className="inline-flex items-center rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300"
                                                title={t('v2.commissioning.history_month.badge_hub_hint')}
                                            >
                                                {t('v2.commissioning.history_month.badge_hub')}
                                            </span>
                                        )}
                                        {otherModeFlags(item).map((f) => (
                                            <ModeFlagBadge key={f.key} flag={f} />
                                        ))}
                                    </div>
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex flex-wrap items-center gap-1">
                                        {resolvedSaleTypeFlags(
                                            item,
                                            t('v2.commissioning.history_month.badge_standard'),
                                            t('v2.commissioning.history_month.badge_sdr'),
                                        ).map((f) => (
                                            <ModeFlagBadge key={f.key} flag={f} />
                                        ))}
                                    </div>
                                </td>
                                <td className="max-w-[220px] truncate px-3 py-2 text-gray-700 dark:text-zinc-200" title={item.deal_title ?? ''}>
                                    {item.deal_title || '—'}
                                    <span className="mt-0.5 block text-[10px] font-normal text-gray-400">
                                        Ploomes #{item.deal_number}
                                    </span>
                                    {item.manual_audit?.included_by_name && (
                                        <span
                                            className="mt-1 block truncate text-[10px] font-medium text-fuchsia-700 dark:text-fuchsia-300"
                                            title={[
                                                item.manual_audit.notes,
                                                item.manual_audit.included_at
                                                    ? new Date(item.manual_audit.included_at).toLocaleString('pt-BR')
                                                    : null,
                                            ].filter(Boolean).join(' · ') || undefined}
                                        >
                                            {t('v2.commissioning.history_month.manual_audit_by', {
                                                name: item.manual_audit.included_by_name,
                                            })}
                                        </span>
                                    )}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2">
                                    <InstallmentBadge item={item} />
                                </td>
                                <td className="px-3 py-2 tabular-nums text-gray-600 dark:text-zinc-300">
                                    {formatDate(clientPayDate(item))}
                                </td>
                                <td className="px-3 py-2 tabular-nums text-gray-600 dark:text-zinc-300">
                                    {formatDate(item.paid_at)}
                                </td>
                                {showRoleColumn && (
                                    <td className="px-3 py-2"><RoleCell item={item} /></td>
                                )}
                                {partyColumns.includes('seller') && (
                                    <td className="px-3 py-2"><SalePartyCell party={item.sale_seller} /></td>
                                )}
                                {partyColumns.includes('manager') && (
                                    <td className="px-3 py-2"><SalePartyCell party={item.sale_manager} /></td>
                                )}
                                <td className="px-3 py-2">
                                    <ValueCell item={item} />
                                </td>
                                <td className="px-3 py-2">
                                    <RateCell item={item} />
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <span
                                        className={cn(
                                            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums',
                                            accent === 'emerald' && 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
                                            accent === 'orange' && 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300',
                                            accent === 'sky' && 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
                                        )}
                                    >
                                        {brl(item.final_amount)}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        {item.is_manual_inclusion && item.manual_inclusion_id && onRemoveManual && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="gap-1 text-red-600 hover:bg-red-50 dark:text-red-300"
                                                onClick={() => onRemoveManual(item.manual_inclusion_id!)}
                                                title={t('v2.commissioning.history_month.manual_include_remove')}
                                            >
                                                <PiTrashBold className="size-3.5" aria-hidden />
                                            </Button>
                                        )}
                                        {!item.is_manual_inclusion && onExcludeFromReport && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="gap-1 text-red-600 hover:bg-red-50 dark:text-red-300"
                                                onClick={() => onExcludeFromReport(item)}
                                                title={t('v2.commissioning.history_month.exclude_from_report')}
                                            >
                                                <PiTrashBold className="size-3.5" aria-hidden />
                                            </Button>
                                        )}
                                        <OpenDealButton item={item} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {hasDefaulted && defaultedItems.map((item, index) => (
                            <tr
                                key={`def-${item.commission_installment_id ?? item.record_id}`}
                                className="bg-red-50/70 dark:bg-red-950/25"
                            >
                                <td className="px-2 py-2 text-center text-xs font-bold tabular-nums text-red-400">
                                    {sorted.length + index + 1}
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        {saleNumbers(item).map((n) => (
                                            <span
                                                key={n}
                                                className="inline-flex items-center rounded-full bg-red-800 px-2.5 py-0.5 text-xs font-bold tabular-nums text-white dark:bg-red-200 dark:text-red-950"
                                            >
                                                {n}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex flex-wrap items-center gap-1">
                                        {(item.mode_flags ?? []).map((f) => (
                                            <ModeFlagBadge key={f.key} flag={f} />
                                        ))}
                                    </div>
                                </td>
                                <td className="max-w-[220px] truncate px-3 py-2 text-red-900 dark:text-red-100" title={item.deal_title ?? ''}>
                                    {item.deal_title || '—'}
                                    <span className="mt-0.5 block text-[10px] font-normal text-red-500/80">
                                        Ploomes #{item.deal_number}
                                    </span>
                                </td>
                                <td className="whitespace-nowrap px-3 py-2">
                                    <InstallmentBadge item={item} tone="red" />
                                </td>
                                <td className="px-3 py-2 tabular-nums text-red-700/80 dark:text-red-300/80">
                                    {formatDate(clientPayDate(item))}
                                </td>
                                <td className="px-3 py-2 tabular-nums text-red-700/80 dark:text-red-300/80">
                                    {formatDate(item.paid_at)}
                                </td>
                                {showRoleColumn && (
                                    <td className="px-3 py-2"><RoleCell item={item} /></td>
                                )}
                                {partyColumns.includes('seller') && (
                                    <td className="px-3 py-2"><SalePartyCell party={item.sale_seller} /></td>
                                )}
                                {partyColumns.includes('manager') && (
                                    <td className="px-3 py-2"><SalePartyCell party={item.sale_manager} /></td>
                                )}
                                <td className="px-3 py-2">
                                    <ValueCell item={item} tone="danger" />
                                </td>
                                <td className="px-3 py-2">
                                    <RateCell item={item} tone="danger" />
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <span className="inline-flex items-center rounded-full bg-red-600 px-2.5 py-0.5 text-xs font-bold tabular-nums text-white dark:bg-red-500">
                                        −{brl(item.final_amount)}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        {onClearDefault && item.commission_installment_id != null && item.commission_installment_id > 0 && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="gap-1 border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-200 dark:hover:bg-red-950"
                                                onClick={() => onClearDefault(item)}
                                                title={t('v2.commissioning.history_month.clear_default')}
                                            >
                                                <PiArrowCounterClockwiseBold className="size-3.5" aria-hidden />
                                                <span className="hidden sm:inline">{t('v2.commissioning.history_month.clear_default')}</span>
                                            </Button>
                                        )}
                                        <OpenDealButton item={item} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {hasForfeited && forfeitedItems.map((item, index) => (
                            <tr
                                key={`forfeit-${item.commission_installment_id ?? item.record_id}`}
                                className="bg-violet-50/70 dark:bg-violet-950/25"
                            >
                                <td className="px-2 py-2 text-center text-xs font-bold tabular-nums text-violet-400">
                                    {sorted.length + defaultedItems.length + index + 1}
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        {saleNumbers(item).map((n) => (
                                            <span
                                                key={n}
                                                className="inline-flex items-center rounded-full bg-violet-800 px-2.5 py-0.5 text-xs font-bold tabular-nums text-white dark:bg-violet-200 dark:text-violet-950"
                                            >
                                                {n}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex flex-wrap items-center gap-1">
                                        {(item.mode_flags ?? []).map((f) => (
                                            <ModeFlagBadge key={f.key} flag={f} />
                                        ))}
                                    </div>
                                </td>
                                <td className="max-w-[220px] truncate px-3 py-2 text-violet-900 dark:text-violet-100" title={item.deal_title ?? ''}>
                                    {item.deal_title || '—'}
                                    <span className="mt-0.5 block text-[10px] font-normal text-violet-500/80">
                                        Ploomes #{item.deal_number}
                                    </span>
                                </td>
                                <td className="whitespace-nowrap px-3 py-2">
                                    <InstallmentBadge item={item} tone="violet" />
                                </td>
                                <td className="px-3 py-2 tabular-nums text-violet-700/80 dark:text-violet-300/80">
                                    {formatDate(clientPayDate(item))}
                                </td>
                                <td className="px-3 py-2 tabular-nums text-violet-700/80 dark:text-violet-300/80">
                                    {formatDate(item.paid_at)}
                                </td>
                                {showRoleColumn && (
                                    <td className="px-3 py-2"><RoleCell item={item} /></td>
                                )}
                                {partyColumns.includes('seller') && (
                                    <td className="px-3 py-2"><SalePartyCell party={item.sale_seller} /></td>
                                )}
                                {partyColumns.includes('manager') && (
                                    <td className="px-3 py-2"><SalePartyCell party={item.sale_manager} /></td>
                                )}
                                <td className="px-3 py-2">
                                    <ValueCell item={item} tone="default" />
                                </td>
                                <td className="px-3 py-2">
                                    <RateCell item={item} tone="default" />
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <span className="inline-flex items-center rounded-full bg-violet-600 px-2.5 py-0.5 text-xs font-bold tabular-nums text-white dark:bg-violet-500">
                                        {brl(0)}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        {canUncancel && item.commission_installment_id != null && item.commission_installment_id > 0 && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="gap-1 border-violet-300 text-violet-700 hover:bg-violet-100 dark:border-violet-700 dark:text-violet-200 dark:hover:bg-violet-950"
                                                onClick={() => setUncancelItem(item)}
                                                title={t('v2.commissioning.history_month.uncancel_commission')}
                                            >
                                                <PiArrowCounterClockwiseBold className="size-3.5" aria-hidden />
                                                <span className="hidden sm:inline">{t('v2.commissioning.history_month.uncancel_commission')}</span>
                                            </Button>
                                        )}
                                        <OpenDealButton item={item} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <HeaderRow border="top" />
                        <tr className="border-t border-gray-200 bg-gray-50 font-bold dark:border-zinc-600 dark:bg-zinc-800/95">
                            <td className="px-3 py-3" colSpan={labelColSpan}>
                                {t('v2.commissioning.history_month.subtotal_normal')}
                                <span className="ml-2 text-xs font-medium text-gray-500">
                                    {t('v2.commissioning.history_month.rows_count', {
                                        count: Math.max(0, items.length - retroactiveItems.length),
                                    })}
                                </span>
                            </td>
                            <td className="px-3 py-3 text-right">
                                <span className="inline-flex items-center rounded-full bg-gray-200 px-3 py-1 text-sm font-black tabular-nums text-gray-800 dark:bg-zinc-700 dark:text-zinc-100">
                                    {brl(normalTotal)}
                                </span>
                            </td>
                            <td />
                        </tr>
                        <tr className="bg-amber-50 font-bold dark:bg-amber-950/40">
                            <td className="px-3 py-3 text-amber-900 dark:text-amber-100" colSpan={labelColSpan}>
                                {t('v2.commissioning.history_month.subtotal_retroactive')}
                                <span className="ml-2 text-xs font-medium text-amber-700/80 dark:text-amber-300/80">
                                    {t('v2.commissioning.history_month.rows_count', { count: retroactiveItems.length })}
                                </span>
                            </td>
                            <td className="px-3 py-3 text-right">
                                <span className="inline-flex items-center rounded-full bg-amber-600 px-3 py-1 text-sm font-black tabular-nums text-white">
                                    {retroactiveTotal > 0 ? `+${brl(retroactiveTotal)}` : brl(0)}
                                </span>
                            </td>
                            <td />
                        </tr>
                        <tr className="bg-slate-100 font-bold dark:bg-zinc-800/90">
                            <td className="px-3 py-3 text-slate-800 dark:text-zinc-100" colSpan={labelColSpan}>
                                {t('v2.commissioning.history_month.subtotal_gross')}
                                <span className="ml-2 text-xs font-medium text-slate-500 dark:text-zinc-400">
                                    {t('v2.commissioning.history_month.gross_hint')}
                                </span>
                            </td>
                            <td className="px-3 py-3 text-right">
                                <span className="inline-flex items-center rounded-full bg-slate-700 px-3 py-1 text-sm font-black tabular-nums text-white dark:bg-zinc-600">
                                    {brl(grossTotal)}
                                </span>
                            </td>
                            <td />
                        </tr>
                        <tr className={cn(
                            'font-bold',
                            hasDefaulted
                                ? 'bg-red-50 dark:bg-red-950/40'
                                : 'bg-red-50/40 dark:bg-red-950/20',
                        )}>
                            <td
                                className={cn(
                                    'px-3 py-3',
                                    hasDefaulted
                                        ? 'text-red-800 dark:text-red-200'
                                        : 'text-red-700/70 dark:text-red-300/70',
                                )}
                                colSpan={labelColSpan}
                            >
                                {t('v2.commissioning.history_month.subtotal_defaulted')}
                                <span className={cn(
                                    'ml-2 text-xs font-medium',
                                    hasDefaulted
                                        ? 'text-red-600/80 dark:text-red-300/80'
                                        : 'text-red-600/50 dark:text-red-300/50',
                                )}>
                                    {t('v2.commissioning.history_month.rows_count', { count: defaultedItems.length })}
                                </span>
                            </td>
                            <td className="px-3 py-3 text-right">
                                <span className={cn(
                                    'inline-flex items-center rounded-full px-3 py-1 text-sm font-black tabular-nums text-white',
                                    hasDefaulted ? 'bg-red-600' : 'bg-red-400/70 dark:bg-red-700/60',
                                )}>
                                    −{brl(defaultedTotal)}
                                </span>
                            </td>
                            <td />
                        </tr>
                        <tr className="bg-violet-50 font-bold dark:bg-violet-950/40">
                            <td className="px-3 py-3 text-violet-800 dark:text-violet-200" colSpan={labelColSpan}>
                                {t('v2.commissioning.history_month.subtotal_forfeited')}
                                <span className="ml-2 text-xs font-medium text-slate-400">
                                    {t('v2.commissioning.history_month.rows_count', { count: forfeitedItems.length })}
                                </span>
                            </td>
                            <td className="px-3 py-3 text-right">
                                <span className="inline-flex items-center rounded-full bg-violet-700 px-3 py-1 text-sm font-black tabular-nums text-white line-through">
                                    {hasForfeited ? brl(forfeitedTotal) : brl(0)}
                                </span>
                            </td>
                            <td />
                        </tr>
                        {hasWallet && (
                            <tr className="bg-rose-50 font-bold dark:bg-rose-950/30">
                                <td className="px-3 py-3 text-rose-800 dark:text-rose-200" colSpan={labelColSpan}>
                                    {t('v2.commissioning.history_month.subtotal_wallet')}
                                    {walletApplied > total && (
                                        <span className="ml-2 text-xs font-medium text-rose-600">
                                            {t('v2.commissioning.history_month.wallet_excess_hint', { amount: brl(walletApplied - total) })}
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-3 text-right">
                                    <span className="inline-flex items-center rounded-full bg-rose-600 px-3 py-1 text-sm font-black tabular-nums text-white">
                                        {walletApplied >= 0 ? '−' : '+'}{brl(Math.abs(walletApplied))}
                                    </span>
                                </td>
                                <td />
                            </tr>
                        )}
                        <tr className={cn(
                            'border-t border-gray-300 font-black dark:border-zinc-500',
                            hasDefaulted || retroactiveTotal > 0
                                ? 'bg-emerald-50/80 dark:bg-emerald-950/30'
                                : 'bg-gray-50 dark:bg-zinc-800/95',
                        )}>
                            <td className="px-3 py-3" colSpan={labelColSpan}>
                                {t('v2.commissioning.history_month.net_total')}
                                <span className="ml-2 text-xs font-medium text-gray-500">
                                    {hasWallet ? t('v2.commissioning.history_month.net_hint_wallet') : t('v2.commissioning.history_month.net_hint')}
                                    {' · '}
                                    {t('v2.commissioning.history_month.rows_count', {
                                        count: items.length,
                                    })}
                                </span>
                            </td>
                            <td className="px-3 py-3 text-right">
                                <span
                                    className={cn(
                                        'inline-flex items-center rounded-full px-3 py-1 text-sm font-black tabular-nums',
                                        accent === 'emerald' && 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
                                        accent === 'orange' && 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300',
                                        accent === 'sky' && 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
                                    )}
                                >
                                    {brl(netTotal)}
                                </span>
                            </td>
                            <td />
                        </tr>
                    </tfoot>
                </table>
            </div>
            <CommissionApprovalModal
                open={uncancelItem != null}
                action="uncancel"
                installmentId={uncancelItem?.commission_installment_id ?? null}
                onClose={() => setUncancelItem(null)}
                onConfirmed={async (code, reason) => {
                    const id = uncancelItem?.commission_installment_id;
                    if (!id) return;
                    await api.post(`/commissioning/installments/${id}/uncancel-commission`, {
                        reason,
                        approval_code: code,
                    });
                    toast.success(t('v2.commissioning.cancelled.undone'));
                    setUncancelItem(null);
                    await queryClient.invalidateQueries({ queryKey: ['commissioning', 'seller-month-history'] });
                }}
            />
        </div>
    );
}

function ItemsTable({ items, forfeitedItems = [], accent }: { items: SellerHistoryMonthItem[]; forfeitedItems?: SellerHistoryMonthItem[]; accent: 'emerald' | 'orange' | 'sky' }) {
    const { t } = useI18n();

    if (items.length === 0 && forfeitedItems.length === 0) {
        return (
            <p className="py-6 text-center text-sm text-gray-400">
                {t('v2.commissioning.history_month.empty')}
            </p>
        );
    }

    const byDeal = new Map<number, SellerHistoryMonthItem[]>();
    for (const item of items) {
        const list = byDeal.get(item.deal_id) ?? [];
        list.push(item);
        byDeal.set(item.deal_id, list);
    }

    const accentText = accentTextClass(accent);

    return (
        <div className="space-y-3">
            {[...byDeal.entries()].map(([dealId, dealItems]) => {
                const head = dealItems[0];
                const total = dealItems.reduce((s, i) => s + i.final_amount, 0);
                return (
                    <article
                        key={dealId}
                        className={cn(
                            'overflow-hidden rounded-xl border',
                            saleTypeRowClass(head)
                                ? 'border-transparent'
                                : 'border-gray-200 dark:border-zinc-700',
                            saleTypeRowClass(head),
                        )}
                    >
                        <header
                            className={cn(
                                'flex flex-wrap items-center justify-between gap-2 px-4 py-2.5',
                                saleTypeRowClass(head)
                                    ? 'bg-black/[0.03] dark:bg-white/[0.04]'
                                    : 'bg-gray-50 dark:bg-zinc-800/70',
                            )}
                        >                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                                        {saleNumber(head)} · {head.deal_title}
                                    </p>
                                    {head.is_legacy_frozen ? (
                                        <span
                                            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-sky-300 bg-sky-100 px-2 py-0.5 text-[10px] font-black text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/20 dark:text-sky-300"
                                            title={t('v2.ploomes_deals.badges.commission_frozen_hint')}
                                        >
                                            <PiSnowflakeBold className="size-3 shrink-0" aria-hidden />
                                            {t('v2.commissioning.history_month.badge_legacy')}
                                        </span>
                                    ) : (
                                        <span
                                            className="inline-flex shrink-0 items-center rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300"
                                            title={t('v2.commissioning.history_month.badge_hub_hint')}
                                        >
                                            {t('v2.commissioning.history_month.badge_hub')}
                                        </span>
                                    )}
                                    {resolvedSaleTypeFlags(
                                        head,
                                        t('v2.commissioning.history_month.badge_standard'),
                                        t('v2.commissioning.history_month.badge_sdr'),
                                    ).map((f) => (
                                        <ModeFlagBadge key={f.key} flag={f} />
                                    ))}
                                    {otherModeFlags(head).map((f) => (
                                        <ModeFlagBadge key={f.key} flag={f} />
                                    ))}
                                </div>
                                <p className="text-[11px] text-gray-500">Ploomes #{head.deal_number}</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <p className={cn('text-sm font-bold tabular-nums', accentText)}>{brl(total)}</p>
                                <OpenDealButton item={head} />
                            </div>
                        </header>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:border-zinc-800">
                                    <th className="px-4 py-2 text-left">{t('v2.commissioning.history_month.col_installment')}</th>
                                    <th className="px-4 py-2 text-left">{t('v2.commissioning.history_month.col_client_date')}</th>
                                    <th className="px-4 py-2 text-left">{t('v2.commissioning.history_month.col_competence')}</th>
                                    <th className="px-4 py-2 text-left">{t('v2.commissioning.history_month.col_role')}</th>
                                    <th className="px-4 py-2 text-right">{t('v2.commissioning.history_month.col_value')}</th>
                                    <th className="px-4 py-2 text-right">{t('v2.commissioning.history_month.col_rate')}</th>
                                    <th className="px-4 py-2 text-right">{t('v2.commissioning.history_month.col_commission')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-zinc-800">
                                {dealItems.map((item) => (
                                    <tr key={item.record_id}>
                                        <td className="px-4 py-2 font-medium">{installmentLabel(item)}</td>
                                        <td className="px-4 py-2 text-gray-500">
                                            {formatDate(clientPayDate(item))}
                                        </td>
                                        <td className="px-4 py-2 text-gray-500">
                                            {formatDate(item.paid_at)}
                                        </td>
                                        <td className="px-4 py-2"><RoleCell item={item} /></td>
                                        <td className="px-4 py-2">
                                            <ValueCell item={item} />
                                        </td>
                                        <td className="px-4 py-2">
                                            <RateCell item={item} />
                                        </td>
                                        <td className={cn('px-4 py-2 text-right font-semibold tabular-nums', item.is_forfeited ? 'text-violet-700 line-through' : accentText)}>
                                            {item.is_forfeited ? brl(0) : brl(item.final_amount)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </article>
                );
            })}
            {forfeitedItems.length > 0 && (
                <div className="space-y-2 rounded-xl border border-violet-200 bg-violet-50 p-3 dark:border-violet-800 dark:bg-violet-950/30">
                    <p className="text-xs font-bold text-violet-800 dark:text-violet-200">
                        {t('v2.commissioning.history_month.forfeited_badge')}
                    </p>
                    {forfeitedItems.map((item) => (
                        <div key={`forfeit-card-${item.commission_installment_id ?? item.record_id}`} className="flex items-center justify-between gap-2 text-sm">
                            <span className="truncate text-violet-900 dark:text-violet-100">
                                {saleNumber(item)} · {item.deal_title || '—'}
                            </span>
                            <span className="shrink-0 font-semibold text-violet-700 line-through dark:text-violet-300">
                                {brl(item.final_amount)}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function SellerCommissionTimelinePage() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { userId, month: monthParam } = useParams<{ userId?: string; month?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const compareParam = searchParams.get('compare');
    const canIncludeManual = isSuperAdmin() && Boolean(userId);
    const [manualModalOpen, setManualModalOpen] = useState(false);

    const defaultMonth = useMemo(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }, []);

    const selectedMonth = monthParam && /^\d{4}-\d{2}$/.test(monthParam) ? monthParam : defaultMonth;
    const [tab, setTab] = useState<'paid' | 'accrued'>('paid');
    const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');

    const originFilter = useMemo<OriginFilter>(() => {
        const raw = searchParams.get('origin');
        return ORIGIN_FILTERS.includes(raw as OriginFilter) ? (raw as OriginFilter) : 'all';
    }, [searchParams]);
    const typeFilter = useMemo<TypeFilter>(() => {
        const raw = searchParams.get('type');
        return TYPE_FILTERS.includes(raw as TypeFilter) ? (raw as TypeFilter) : 'all';
    }, [searchParams]);
    const roleFilter = useMemo<RoleFilter>(() => {
        const raw = searchParams.get('role');
        return ROLE_FILTERS.includes(raw as RoleFilter) ? (raw as RoleFilter) : 'all';
    }, [searchParams]);
    const retroFilter = useMemo<RetroFilter>(() => {
        const raw = searchParams.get('retro');
        return RETRO_FILTERS.includes(raw as RetroFilter) ? (raw as RetroFilter) : 'all';
    }, [searchParams]);
    const sellerFilter = useMemo(
        () => parsePartyFilterParam(searchParams.get('sellers')),
        [searchParams],
    );
    const managerFilter = useMemo(
        () => parsePartyFilterParam(searchParams.get('managers')),
        [searchParams],
    );

    const setOriginFilter = (origin: OriginFilter) => {
        const next = new URLSearchParams(searchParams);
        if (origin === 'all') next.delete('origin');
        else next.set('origin', origin);
        setSearchParams(next, { replace: true });
    };
    const setTypeFilter = (type: TypeFilter) => {
        const next = new URLSearchParams(searchParams);
        if (type === 'all') next.delete('type');
        else next.set('type', type);
        setSearchParams(next, { replace: true });
    };
    const setRoleFilter = (role: RoleFilter) => {
        const next = new URLSearchParams(searchParams);
        if (role === 'all') next.delete('role');
        else next.set('role', role);
        setSearchParams(next, { replace: true });
    };
    const setRetroFilter = (retro: RetroFilter) => {
        const next = new URLSearchParams(searchParams);
        if (retro === 'all') next.delete('retro');
        else next.set('retro', retro);
        setSearchParams(next, { replace: true });
    };
    const setSellerFilter = (values: string[]) => {
        const next = new URLSearchParams(searchParams);
        writePartyFilterParam(next, 'sellers', values);
        setSearchParams(next, { replace: true });
    };
    const setManagerFilter = (values: string[]) => {
        const next = new URLSearchParams(searchParams);
        writePartyFilterParam(next, 'managers', values);
        setSearchParams(next, { replace: true });
    };

    const basePath = userId
        ? `/commissioning/sellers/${userId}/history`
        : '/my-commission/history';

    const { data: timelineRes, isLoading: loadingTimeline } = useQuery<{ data: TimelinePayload }>({
        queryKey: ['commissioning', 'seller-timeline', userId ?? 'me'],
        queryFn: () => {
            const path = userId
                ? `/commissioning/sellers/${userId}/profile/timeline`
                : '/commissioning/my-seller-profile/timeline';
            return api.get(path, { params: { months: 18 } }).then((r) => r.data);
        },
        ...COMMISSION_HOME_MONTH_QUERY,
    });

    const timeline = timelineRes?.data;

    const detailQuery = useQuery<{ data: SellerHistoryMonthDetail | ComparePayload }>({
        queryKey: ['commissioning', 'seller-month-history', userId ?? 'me', selectedMonth, compareParam],
        queryFn: () => {
            const path = userId
                ? `/commissioning/sellers/${userId}/profile/months/${selectedMonth}`
                : `/commissioning/my-seller-profile/months/${selectedMonth}`;
            return api.get(path, {
                params: compareParam ? { compare: compareParam } : undefined,
            }).then((r) => r.data);
        },
        ...COMMISSION_HOME_MONTH_QUERY,
    });

    const detailRaw = detailQuery.data?.data;
    const isCompare = !!(detailRaw && 'delta' in detailRaw);
    const detail = isCompare
        ? (detailRaw as ComparePayload).base
        : (detailRaw as SellerHistoryMonthDetail | undefined);
    const compareDetail = isCompare ? (detailRaw as ComparePayload).compare : null;
    const delta = isCompare ? (detailRaw as ComparePayload).delta : null;

    const selectMonth = (ym: string) => {
        const qs = searchParams.toString();
        navigate(`${basePath}/${ym}${qs ? `?${qs}` : ''}`);
    };

    const setCompare = (ym: string | null) => {
        const next = new URLSearchParams(searchParams);
        if (ym) next.set('compare', ym);
        else next.delete('compare');
        setSearchParams(next, { replace: true });
    };

    const swapMonths = () => {
        if (!compareParam || compareParam === selectedMonth) return;
        const next = new URLSearchParams(searchParams);
        next.set('compare', selectedMonth);
        const qs = next.toString();
        navigate(`${basePath}/${compareParam}${qs ? `?${qs}` : ''}`);
    };

    const monthDelta = timeline?.month_deltas.find((d) => d.month === selectedMonth);

    const sourceItems = useMemo(() => {
        if (!detail) return [] as SellerHistoryMonthItem[];
        return tab === 'paid' ? detail.paid_items : detail.accrued_items;
    }, [detail, tab]);

    const filteredItems = useMemo(
        () => sourceItems.filter((i) => matchesHistoryFilters(
            i, originFilter, typeFilter, roleFilter, retroFilter, sellerFilter, managerFilter,
        )),
        [sourceItems, originFilter, typeFilter, roleFilter, retroFilter, sellerFilter, managerFilter],
    );

    const compareSourceItems = useMemo(() => {
        if (!compareDetail) return [] as SellerHistoryMonthItem[];
        return tab === 'paid' ? compareDetail.paid_items : compareDetail.accrued_items;
    }, [compareDetail, tab]);

    const filteredCompareItems = useMemo(
        () => compareSourceItems.filter((i) => matchesHistoryFilters(
            i, originFilter, typeFilter, roleFilter, retroFilter, sellerFilter, managerFilter,
        )),
        [compareSourceItems, originFilter, typeFilter, roleFilter, retroFilter, sellerFilter, managerFilter],
    );

    const filteredDefaultedItems = useMemo(
        () => (detail?.defaulted_items ?? []).filter((i) => matchesHistoryFilters(
            i, originFilter, typeFilter, roleFilter, retroFilter, sellerFilter, managerFilter,
        )),
        [detail?.defaulted_items, originFilter, typeFilter, roleFilter, retroFilter, sellerFilter, managerFilter],
    );
    const filteredCompareDefaultedItems = useMemo(
        () => (compareDetail?.defaulted_items ?? []).filter((i) => matchesHistoryFilters(
            i, originFilter, typeFilter, roleFilter, retroFilter, sellerFilter, managerFilter,
        )),
        [compareDetail?.defaulted_items, originFilter, typeFilter, roleFilter, retroFilter, sellerFilter, managerFilter],
    );
    const filteredForfeitedItems = useMemo(
        () => (detail?.forfeited_items ?? []).filter((i) => matchesHistoryFilters(
            i, originFilter, typeFilter, roleFilter, retroFilter, sellerFilter, managerFilter,
        )),
        [detail?.forfeited_items, originFilter, typeFilter, roleFilter, retroFilter, sellerFilter, managerFilter],
    );
    const filteredCompareForfeitedItems = useMemo(
        () => (compareDetail?.forfeited_items ?? []).filter((i) => matchesHistoryFilters(
            i, originFilter, typeFilter, roleFilter, retroFilter, sellerFilter, managerFilter,
        )),
        [compareDetail?.forfeited_items, originFilter, typeFilter, roleFilter, retroFilter, sellerFilter, managerFilter],
    );

    const removeManualMutation = useMutation({
        mutationFn: (inclusionId: number) =>
            api.delete(`/commissioning/sellers/${userId}/manual-inclusions/${inclusionId}`),
        onSuccess: async () => {
            toast.success(t('v2.commissioning.history_month.manual_include_removed'));
            await queryClient.invalidateQueries({
                queryKey: ['commissioning', 'seller-month-history', userId ?? 'me', selectedMonth],
            });
        },
        onError: () => toast.error(t('v2.commissioning.history_month.exclude_error')),
    });

    const [excludeItem, setExcludeItem] = useState<SellerHistoryMonthItem | null>(null);
    const [excludeReason, setExcludeReason] = useState('');
    const [excludeAlsoFuture, setExcludeAlsoFuture] = useState(false);
    const excludeAlsoFutureRef = useRef(false);
    const excludeFutureAutoKeyRef = useRef<string | null>(null);
    const [clearDefaultItem, setClearDefaultItem] = useState<SellerHistoryMonthItem | null>(null);
    const [clearDefaultReason, setClearDefaultReason] = useState('');
    const [clearDefaultPaidAt, setClearDefaultPaidAt] = useState('');

    const setAlsoFuture = (next: boolean) => {
        excludeAlsoFutureRef.current = next;
        setExcludeAlsoFuture(next);
    };

    const excludeFuturePreviewQuery = useQuery({
        queryKey: [
            'commissioning',
            'month-exclusion-future',
            userId,
            selectedMonth,
            excludeItem?.deal_id,
        ],
        enabled: Boolean(canIncludeManual && userId && excludeItem?.deal_id && selectedMonth),
        queryFn: async () => {
            const { data } = await api.get<{
                data: {
                    count: number;
                    months: string[];
                    total_amount: number;
                };
            }>(`/commissioning/sellers/${userId}/month-exclusions/${selectedMonth}/future`, {
                params: { ploomes_deal_id: excludeItem!.deal_id },
            });
            return data.data;
        },
        retry: 1,
    });

    const futureExcludeCount = excludeFuturePreviewQuery.data?.count ?? 0;

    // Liga o toggle uma vez por venda aberta no modal, quando o preview chega.
    useEffect(() => {
        if (!excludeItem) {
            excludeFutureAutoKeyRef.current = null;
            return;
        }
        if (!excludeFuturePreviewQuery.isSuccess || excludeFuturePreviewQuery.isFetching) {
            return;
        }
        const key = `${excludeItem.deal_id}:${excludeItem.record_id}:${selectedMonth}`;
        if (excludeFutureAutoKeyRef.current === key) {
            return;
        }
        excludeFutureAutoKeyRef.current = key;
        setAlsoFuture(futureExcludeCount > 0);
    }, [
        excludeItem,
        selectedMonth,
        futureExcludeCount,
        excludeFuturePreviewQuery.isSuccess,
        excludeFuturePreviewQuery.isFetching,
    ]);

    const excludeMutation = useMutation({
        mutationFn: (payload: {
            ploomes_deal_id: number;
            commission_installment_id: number | null;
            commission_record_id: number | null;
            reason: string;
            also_future_months: boolean;
        }) => api.post<{
            data: {
                future_excluded_count?: number;
                future_excluded_months?: string[];
            };
        }>(`/commissioning/sellers/${userId}/month-exclusions/${selectedMonth}`, payload),
        onSuccess: async (res) => {
            const payload = res.data?.data ?? res.data;
            const futureCount = Number(
                (payload as { future_excluded_count?: number })?.future_excluded_count ?? 0,
            );
            toast.success(
                futureCount > 0
                    ? t('v2.commissioning.history_month.exclude_ok_with_future', { count: futureCount })
                    : t('v2.commissioning.history_month.exclude_ok'),
            );
            setExcludeItem(null);
            setExcludeReason('');
            setAlsoFuture(false);
            await queryClient.invalidateQueries({
                queryKey: ['commissioning', 'seller-month-history', userId ?? 'me'],
            });
            await queryClient.invalidateQueries({
                queryKey: ['commissioning', 'seller-timeline', userId ?? 'me'],
            });
            await queryClient.invalidateQueries({
                queryKey: ['commissioning', 'month-exclusion-future', userId],
            });
        },
        onError: (err: unknown) => {
            const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
                ?.response?.data;
            const firstFieldError = data?.errors
                ? Object.values(data.errors).flat()[0]
                : null;
            toast.error(firstFieldError || data?.message || t('v2.commissioning.history_month.exclude_error'));
        },
    });

    const canToggleFuture = futureExcludeCount > 0 && !excludeMutation.isPending;

    const clearDefaultMutation = useMutation({
        mutationFn: (payload: { installmentId: number; reason: string; paid_at?: string }) =>
            api.post(`/commissioning/installments/${payload.installmentId}/clear-default`, {
                reason: payload.reason,
                ...(payload.paid_at ? { paid_at: payload.paid_at } : {}),
            }),
        onSuccess: async () => {
            toast.success(t('v2.commissioning.history_month.clear_default_ok'));
            setClearDefaultItem(null);
            setClearDefaultReason('');
            setClearDefaultPaidAt('');
            await queryClient.invalidateQueries({
                queryKey: ['commissioning', 'seller-month-history', userId ?? 'me', selectedMonth],
            });
            await queryClient.invalidateQueries({
                queryKey: ['commissioning', 'seller-timeline', userId ?? 'me'],
            });
        },
        onError: (err: unknown) => {
            const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
                ?.response?.data;
            const firstFieldError = data?.errors
                ? Object.values(data.errors).flat()[0]
                : null;
            toast.error(firstFieldError || data?.message || t('v2.commissioning.history_month.clear_default_error'));
        },
    });

    const stripMonths = useMemo(
        () => buildStripMonths(timeline?.months ?? [], timeline?.forecast),
        [timeline?.months, timeline?.forecast],
    );

    const selectedStrip = stripMonths.find((m) => m.month === selectedMonth);
    const detailPaid = detail?.net_paid_total ?? detail?.paid_total;
    const monthCommission = (detailPaid != null && detailPaid > 0)
        ? detailPaid
        : (selectedStrip?.display ?? 0);
    const futureAmount = timeline?.summary.pipeline ?? timeline?.forecast?.total_remaining ?? 0;
    const futureCount = timeline?.forecast?.horizon_installments_count
        ?? timeline?.forecast?.installments_count
        ?? 0;
    const defaultedAmount = detail?.defaulted_total ?? 0;
    const defaultedCount = detail?.defaulted_count ?? 0;

    return (
        <div className="space-y-5 pb-10">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3">
                    <Link
                        to={userId ? `/commissioning/sellers/${userId}` : '/my-commission'}
                        className="mt-1 flex size-9 shrink-0 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-zinc-800"
                    >
                        <PiArrowLeftBold className="size-4" />
                    </Link>
                    <div className="min-w-0">
                        <h1 className="text-[22px] font-bold leading-tight text-gray-900 lg:text-2xl dark:text-white">
                            {t('v2.commissioning.timeline.title')}
                        </h1>
                        <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
                            {t('v2.commissioning.timeline.subtitle')}
                        </p>
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-2 self-start sm:self-auto">
                    <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100">
                        <PiCalendarBlankBold className="size-4 text-gray-400" aria-hidden />
                        <span className="capitalize">{fmtMonthLong(selectedMonth)}</span>
                    </div>
                </div>
            </div>

            {loadingTimeline && <LoadingState variant="dashboard" />}

            {timeline && (
                <>
                    <MonthStrip
                        months={stripMonths}
                        selected={selectedMonth}
                        compare={compareParam}
                        onSelect={selectMonth}
                    />

                    <SummaryHero
                        user={timeline.user}
                        team={timeline.team}
                        monthCommission={monthCommission}
                        futureAmount={futureAmount}
                        futureCount={futureCount}
                        defaultedAmount={defaultedAmount}
                        defaultedCount={defaultedCount}
                    />

                    <MonthComparator
                        months={stripMonths}
                        selected={selectedMonth}
                        compare={compareParam}
                        onSelect={selectMonth}
                        onCompare={setCompare}
                        onSwap={swapMonths}
                    />

                    <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <h2 className="text-base font-bold capitalize text-gray-900 dark:text-white">
                                    {fmtMonthShort(selectedMonth)}
                                </h2>
                                {monthDelta && (
                                    <div className="mt-1">
                                        <DirectionBadge
                                            direction={monthDelta.direction}
                                            label={
                                                monthDelta.direction === 'flat'
                                                    ? t('v2.commissioning.timeline.flat_vs_prev')
                                                    : t('v2.commissioning.timeline.delta_vs_prev', {
                                                        delta: brlShort(Math.abs(monthDelta.delta)),
                                                        pct: monthDelta.delta_pct != null
                                                            ? `${monthDelta.delta_pct > 0 ? '+' : ''}${monthDelta.delta_pct}%`
                                                            : '—',
                                                    })
                                            }
                                        />
                                    </div>
                                )}
                            </div>

                            {delta && compareDetail && (
                                <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-2 dark:border-sky-800 dark:bg-sky-950/30">
                                    <p className="text-[10px] font-semibold uppercase text-sky-700 dark:text-sky-300">
                                        {fmtMonthShort(selectedMonth)} vs {fmtMonthShort(compareDetail.month)}
                                    </p>
                                    <div className="mt-1 flex flex-wrap items-center gap-2">
                                        <DirectionBadge
                                            direction={delta.direction_paid}
                                            label={`${delta.paid >= 0 ? '+' : ''}${brlShort(delta.paid)}${
                                                delta.paid_pct != null ? ` (${delta.paid_pct > 0 ? '+' : ''}${delta.paid_pct}%)` : ''
                                            }`}
                                        />
                                        <span className="text-xs text-gray-500">
                                            {brlShort(detail?.paid_total ?? 0)} vs {brlShort(compareDetail.paid_total)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {detailQuery.isLoading && <LoadingState variant="detail" />}

                        {detail && !detailQuery.isLoading && (
                            <>
                                <MonthClosedBadge closing={detail.closing} />
                                <div className="mb-3 flex justify-end">
                                    <StatementPdfButton userId={userId} month={selectedMonth} />
                                </div>
                                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex flex-1 gap-1 rounded-xl bg-gray-100 p-1 dark:bg-zinc-800">
                                        <button
                                            type="button"
                                            onClick={() => setTab('paid')}
                                            className={cn(
                                                'flex-1 rounded-lg px-3 py-2 text-xs font-semibold',
                                                tab === 'paid'
                                                    ? 'bg-white text-emerald-700 shadow-sm dark:bg-zinc-900'
                                                    : 'text-gray-500',
                                            )}
                                        >
                                            Pago · {brlShort(detail.paid_total_after_wallet ?? detail.paid_total)} ({detail.paid_count})
                                            {(detail.forfeited_total ?? 0) > 0 && (
                                                <span className="ml-1 text-violet-700">
                                                    · {t('v2.commissioning.history_month.forfeited_badge')} {brlShort(detail.forfeited_total ?? 0)}
                                                </span>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setTab('accrued')}
                                            className={cn(
                                                'flex-1 rounded-lg px-3 py-2 text-xs font-semibold',
                                                tab === 'accrued'
                                                    ? 'bg-white text-orange-700 shadow-sm dark:bg-zinc-900'
                                                    : 'text-gray-500',
                                            )}
                                        >
                                            Gerado · {brlShort(detail.accrued_total)} ({detail.accrued_count})
                                        </button>
                                    </div>

                                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                                        {canIncludeManual && tab === 'paid' && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="gap-1.5"
                                                onClick={() => setManualModalOpen(true)}
                                            >
                                                <PiPlusBold className="size-3.5" />
                                                {t('v2.commissioning.history_month.manual_include_btn')}
                                            </Button>
                                        )}
                                        <div className="flex shrink-0 gap-1 rounded-xl border border-gray-200 bg-white p-1 dark:border-zinc-700 dark:bg-zinc-900">
                                        <button
                                            type="button"
                                            onClick={() => setViewMode('table')}
                                            className={cn(
                                                'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold',
                                                viewMode === 'table'
                                                    ? 'bg-orange-500 text-white shadow-sm'
                                                    : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-zinc-800',
                                            )}
                                        >
                                            <PiTableBold className="size-3.5" />
                                            {t('v2.commissioning.history_month.view_table')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setViewMode('cards')}
                                            className={cn(
                                                'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold',
                                                viewMode === 'cards'
                                                    ? 'bg-orange-500 text-white shadow-sm'
                                                    : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-zinc-800',
                                            )}
                                        >
                                            <PiCardsBold className="size-3.5" />
                                            {t('v2.commissioning.history_month.view_cards')}
                                        </button>
                                        </div>
                                    </div>
                                </div>

                                <p className="mb-3 text-xs text-gray-500 dark:text-zinc-400">
                                    {tab === 'paid'
                                        ? t('v2.commissioning.history_month.hint_paid')
                                        : t('v2.commissioning.history_month.hint_accrued')}
                                </p>

                                {tab === 'paid' && userId && (
                                    <CommissionWalletPanel
                                        sellerUserId={Number(userId)}
                                        wallet={detail?.wallet}
                                        applied={detail?.wallet_applied_total}
                                        afterWallet={detail?.paid_total_after_wallet}
                                        month={selectedMonth}
                                    />
                                )}

                                {tab === 'paid' && (
                                    <WalletAppliedSection
                                        sellerUserId={userId ? Number(userId) : null}
                                        month={selectedMonth}
                                        items={detail?.wallet_applied_items}
                                        excess={detail?.wallet_excess}
                                        closed={!!detail?.closing}
                                    />
                                )}

                                <HistoryMonthFilters
                                    items={sourceItems}
                                    filterParties={detail?.filter_parties}
                                    origin={originFilter}
                                    type={typeFilter}
                                    role={roleFilter}
                                    retro={retroFilter}
                                    sellers={sellerFilter}
                                    managers={managerFilter}
                                    partyColumns={detail?.party_columns ?? []}
                                    onOriginChange={setOriginFilter}
                                    onTypeChange={setTypeFilter}
                                    onRoleChange={setRoleFilter}
                                    onRetroChange={setRetroFilter}
                                    onSellersChange={setSellerFilter}
                                    onManagersChange={setManagerFilter}
                                />

                                <div className={cn('grid gap-4', compareDetail && viewMode === 'cards' && 'lg:grid-cols-2')}>
                                    <div>
                                        {compareDetail && (
                                            <p className="mb-2 text-xs font-semibold text-orange-700">
                                                {fmtMonthShort(selectedMonth)} (base)
                                            </p>
                                        )}
                                        {viewMode === 'table' ? (
                                            <FlatMonthTable
                                                items={filteredItems}
                                                defaultedItems={tab === 'paid' ? filteredDefaultedItems : []}
                                                forfeitedItems={tab === 'paid' ? filteredForfeitedItems : []}
                                                accent={tab === 'paid' ? 'emerald' : 'orange'}
                                                sellerName={timeline?.user.name ?? ''}
                                                sellerUserId={userId ? Number(userId) : null}
                                                month={selectedMonth}
                                                partyColumns={detail?.party_columns ?? []}
                                                walletApplied={tab === 'paid' ? (detail?.wallet_applied_total ?? 0) : 0}
                                                onRemoveManual={
                                                    canIncludeManual
                                                        ? (id) => removeManualMutation.mutate(id)
                                                        : undefined
                                                }
                                                onExcludeFromReport={
                                                    canIncludeManual && tab === 'paid'
                                                        ? (item) => {
                                                            setAlsoFuture(false);
                                                            setExcludeReason('');
                                                            setExcludeItem(item);
                                                        }
                                                        : undefined
                                                }
                                                onClearDefault={
                                                    canIncludeManual && tab === 'paid'
                                                        ? (item) => {
                                                            setClearDefaultItem(item);
                                                            setClearDefaultReason('');
                                                            setClearDefaultPaidAt('');
                                                        }
                                                        : undefined
                                                }
                                            />
                                        ) : (
                                            <ItemsTable
                                                items={filteredItems}
                                                forfeitedItems={tab === 'paid' ? filteredForfeitedItems : []}
                                                accent={tab === 'paid' ? 'emerald' : 'orange'}
                                            />
                                        )}
                                    </div>
                                    {compareDetail && (
                                        <div>
                                            <p className="mb-2 text-xs font-semibold text-sky-700">
                                                {fmtMonthShort(compareDetail.month)} (comparar)
                                            </p>
                                            {viewMode === 'table' ? (
                                                <FlatMonthTable
                                                    items={filteredCompareItems}
                                                    defaultedItems={tab === 'paid' ? filteredCompareDefaultedItems : []}
                                                    forfeitedItems={tab === 'paid' ? filteredCompareForfeitedItems : []}
                                                    accent="sky"
                                                    sellerName={timeline?.user.name ?? ''}
                                                    sellerUserId={userId ? Number(userId) : null}
                                                    month={compareDetail.month}
                                                    partyColumns={compareDetail.party_columns ?? []}
                                                />
                                            ) : (
                                                <ItemsTable
                                                    forfeitedItems={tab === 'paid' ? filteredCompareForfeitedItems : []}
                                                    items={filteredCompareItems}
                                                    accent="sky"
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </section>
                </>
            )}

            {canIncludeManual && userId && (
                <ManualSaleInclusionModal
                    open={manualModalOpen}
                    onClose={() => setManualModalOpen(false)}
                    sellerUserId={Number(userId)}
                    month={selectedMonth}
                    sellerName={timeline?.user.name}
                    allowOtherSellers={(detail?.party_columns?.length ?? 0) > 0}
                />
            )}

            <ConfirmModal
                open={Boolean(excludeItem)}
                onClose={() => {
                    if (excludeMutation.isPending) return;
                    setExcludeItem(null);
                    setExcludeReason('');
                    setAlsoFuture(false);
                }}
                onConfirm={() => {
                    if (!excludeItem || !userId) return;
                    const reason = excludeReason.trim();
                    if (reason.length < 3) {
                        toast.error(t('v2.commissioning.history_month.exclude_reason_required'));
                        return;
                    }
                    const installmentId = excludeItem.commission_installment_id;
                    excludeMutation.mutate({
                        ploomes_deal_id: excludeItem.deal_id,
                        commission_installment_id:
                            installmentId != null && installmentId > 0 ? installmentId : null,
                        commission_record_id:
                            excludeItem.record_id > 0 ? excludeItem.record_id : null,
                        reason,
                        also_future_months: Boolean(excludeAlsoFutureRef.current),
                    });
                }}
                title={t('v2.commissioning.history_month.exclude_title')}
                description={t('v2.commissioning.history_month.exclude_help')}
                confirmLabel={t('v2.commissioning.history_month.exclude_confirm')}
                cancelLabel={t('v2.commissioning.history_month.manual_include_cancel')}
                loading={excludeMutation.isPending}
            >
                <div className="space-y-3">
                    {excludeItem && (
                        <p className="text-sm text-gray-700 dark:text-zinc-200">
                            {saleNumber(excludeItem)} · {excludeItem.deal_title || '—'} · {brl(excludeItem.final_amount)}
                        </p>
                    )}
                    <textarea
                        value={excludeReason}
                        onChange={(e) => setExcludeReason(e.target.value)}
                        rows={3}
                        placeholder={t('v2.commissioning.history_month.exclude_reason_ph')}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                    />
                    <div
                        role="group"
                        className={cn(
                            'flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                            canToggleFuture
                                ? 'cursor-pointer border-indigo-200 bg-indigo-50/80 hover:bg-indigo-50 dark:border-indigo-500/40 dark:bg-indigo-950/30 dark:hover:bg-indigo-950/50'
                                : 'border-gray-200 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800/50',
                        )}
                        onClick={() => {
                            if (!canToggleFuture) return;
                            setAlsoFuture(!excludeAlsoFuture);
                        }}
                    >
                        <div className="min-w-0">
                            <p
                                className={cn(
                                    'text-sm font-semibold',
                                    canToggleFuture
                                        ? 'text-indigo-950 dark:text-indigo-100'
                                        : 'text-gray-500 dark:text-zinc-400',
                                )}
                            >
                                {t('v2.commissioning.history_month.exclude_also_future')}
                            </p>
                            {excludeFuturePreviewQuery.isFetching ? (
                                <p className="mt-0.5 text-xs font-medium text-gray-500 dark:text-zinc-400">
                                    {t('v2.commissioning.history_month.exclude_future_loading')}
                                </p>
                            ) : excludeFuturePreviewQuery.isError ? (
                                <p className="mt-0.5 text-xs font-medium text-red-600 dark:text-red-300">
                                    {t('v2.commissioning.history_month.exclude_future_error')}
                                </p>
                            ) : futureExcludeCount > 0 ? (
                                <p className="mt-0.5 text-xs font-medium text-indigo-800/80 dark:text-indigo-200/80">
                                    {t('v2.commissioning.history_month.exclude_also_future_hint', {
                                        count: futureExcludeCount,
                                        months: (excludeFuturePreviewQuery.data?.months ?? []).join(', '),
                                        amount: brl(excludeFuturePreviewQuery.data?.total_amount ?? 0),
                                    })}
                                </p>
                            ) : (
                                <p className="mt-0.5 text-xs font-medium text-gray-500 dark:text-zinc-400">
                                    {t('v2.commissioning.history_month.exclude_also_future_none')}
                                </p>
                            )}
                        </div>
                        <span
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="shrink-0"
                        >
                            <FutureMonthsToggle
                                checked={excludeAlsoFuture}
                                disabled={!canToggleFuture}
                                label={t('v2.commissioning.history_month.exclude_also_future')}
                                onChange={setAlsoFuture}
                            />
                        </span>
                    </div>
                </div>
            </ConfirmModal>

            <ConfirmModal
                open={Boolean(clearDefaultItem)}
                onClose={() => {
                    if (clearDefaultMutation.isPending) return;
                    setClearDefaultItem(null);
                    setClearDefaultReason('');
                    setClearDefaultPaidAt('');
                }}
                onConfirm={() => {
                    if (!clearDefaultItem) return;
                    const reason = clearDefaultReason.trim();
                    if (reason.length < 3) {
                        toast.error(t('v2.commissioning.history_month.clear_default_reason_required'));
                        return;
                    }
                    const installmentId = clearDefaultItem.commission_installment_id;
                    if (installmentId == null || installmentId <= 0) {
                        toast.error(t('v2.commissioning.history_month.clear_default_error'));
                        return;
                    }
                    clearDefaultMutation.mutate({
                        installmentId,
                        reason,
                        paid_at: clearDefaultPaidAt.trim() || undefined,
                    });
                }}
                title={t('v2.commissioning.history_month.clear_default_title')}
                description={t('v2.commissioning.history_month.clear_default_help')}
                confirmLabel={t('v2.commissioning.history_month.clear_default_confirm')}
                cancelLabel={t('v2.commissioning.history_month.manual_include_cancel')}
                loading={clearDefaultMutation.isPending}
            >
                <div className="space-y-2">
                    {clearDefaultItem && (
                        <p className="text-sm text-gray-700 dark:text-zinc-200">
                            {saleNumber(clearDefaultItem)} · {clearDefaultItem.deal_title || '—'} · −{brl(clearDefaultItem.final_amount)}
                        </p>
                    )}
                    <label className="block text-xs font-medium text-gray-500">
                        {t('v2.commissioning.history_month.clear_default_paid_at')}
                    </label>
                    <input
                        type="date"
                        value={clearDefaultPaidAt}
                        onChange={(e) => setClearDefaultPaidAt(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                    />
                    <p className="text-[11px] text-gray-400">{t('v2.commissioning.history_month.clear_default_paid_at_hint')}</p>
                    <textarea
                        value={clearDefaultReason}
                        onChange={(e) => setClearDefaultReason(e.target.value)}
                        rows={3}
                        placeholder={t('v2.commissioning.history_month.clear_default_reason_ph')}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                    />
                </div>
            </ConfirmModal>
        </div>
    );
}

/** Holerite do mês em PDF (mesmos números da tela; snapshot quando o mês está fechado). */
function StatementPdfButton({ userId, month }: { userId?: string; month: string }) {
    const { t } = useI18n();
    const [loading, setLoading] = useState(false);
    const path = userId
        ? `/commissioning/sellers/${userId}/profile/months/${month}/statement`
        : `/commissioning/my-seller-profile/months/${month}/statement`;

    return (
        <Button
            type="button"
            size="sm"
            variant="outline"
            isLoading={loading}
            onClick={async () => {
                setLoading(true);
                try {
                    await downloadPdf(path, `holerite-comissao-${month}.pdf`);
                } catch {
                    toast.error(t('v2.gluteleader_wallet.statement_failed'));
                } finally {
                    setLoading(false);
                }
            }}
        >
            <PiFilePdfBold className="mr-1 size-4" aria-hidden />
            {t('v2.commissioning.history_month.download_statement')}
        </Button>
    );
}
